// src/components/CommunityView.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { useRoutePortalTarget } from "./layout/RoutePortalHost";
import {
  View,
  type CommunityPost,
  type UserProfile,
  type AppUser,
  type Comment,
  type ToastType,
  type CommunityMention,
  type UserReportReason,
} from "../types";
import {
  MessageSquareIcon,
  ShareIcon,
  XIcon,
  SearchIcon,
  PlusCircleIcon,
} from "./icons";
import {
  followUser,
  unfollowUser,
  subscribeToFollowing,
} from "../services/following";
import { submitUserReport } from "../services/reports";
import {
  subscribeMyPostLikes,
  subscribeMyCommentLikes,
} from "../services/community";
import {
  getRoddoTheme,
  getRoddoUiTokens,
  resolveRoddoDarkMode,
} from "../constants/theme";
import ConfirmationModal from "./ConfirmationModal";
import { uploadImage } from "../services/imageUpload";
import { compressImageFile } from "../utils/imageCompress";
import { buildPublicPostUrl, share as shareOut } from "../utils/share";
import { txt } from "../constants/typography";
import type { ReputationSummary } from "../types";
import { getFeaturedBadges, buildReputationSummary } from "../utils/reputation";
import { FEATURES } from "../constants/features";
import NativeAdCard from "./NativeAdCard";
import { insertNativeFeedAdSlots } from "../services/ads";

declare const __BUILD_ID__: string;

type CommunityTabId = "feed" | "following" | "trending";
type ReplyTarget = {
  commentId: string;
  authorName: string;
  authorHandle?: string | null;
} | null;

function normalizeInitialCommunityTab(value: any): CommunityTabId {
  if (value === "following") return "following";
  if (value === "trending") return "trending";
  // A future crew-only lane can live here later. For launch, Crew opens the stable main feed.
  return "feed";
}


type CommunityBayId =
  | "global"
  | "north-america"
  | "south-america"
  | "europe"
  | "asia"
  | "oceania"
  | "africa";

type CommunityDockId =
  | "main"
  | "catch-wall"
  | "catch-help"
  | "tackle-talk"
  | "events";

const COMMUNITY_BAY_STORAGE_KEY = "roddo.community.selectedBay.v1";
const COMMUNITY_DOCK_STORAGE_KEY = "roddo.community.selectedDock.v1";

const COMMUNITY_BAYS: Array<{ id: CommunityBayId; label: string; shortLabel: string; emoji: string; helper: string }> = [
  { id: "global", label: "Global Bay", shortLabel: "Global", emoji: "🌎", helper: "Worldwide Roddo posts" },
  { id: "north-america", label: "North America Bay", shortLabel: "N. America", emoji: "🌲", helper: "North America catches and chatter" },
  { id: "south-america", label: "South America Bay", shortLabel: "S. America", emoji: "🌴", helper: "South America waters" },
  { id: "europe", label: "Europe Bay", shortLabel: "Europe", emoji: "🌊", helper: "Europe fishing community" },
  { id: "asia", label: "Asia Bay", shortLabel: "Asia", emoji: "🏯", helper: "Asia fishing community" },
  { id: "oceania", label: "Oceania Bay", shortLabel: "Oceania", emoji: "🐨", helper: "Australia, NZ, and Pacific waters" },
  { id: "africa", label: "Africa Bay", shortLabel: "Africa", emoji: "🦁", helper: "Africa fishing community" },
];

const COMMUNITY_DOCKS: Array<{ id: CommunityDockId; label: string; shortLabel: string; emoji: string; helper: string }> = [
  { id: "main", label: "Main Dock", shortLabel: "Main", emoji: "🐳", helper: "General Baleen Bay chatter" },
  { id: "catch-wall", label: "Catch Wall", shortLabel: "Catches", emoji: "📸", helper: "Show catches and field notes" },
  { id: "catch-help", label: "Catch Help", shortLabel: "Help", emoji: "🎣", helper: "IDs, tactics, and fishing questions" },
  { id: "tackle-talk", label: "Tackle Talk", shortLabel: "Tackle", emoji: "🧰", helper: "Rods, reels, lures, and setup talk" },
  { id: "events", label: "Events Pier", shortLabel: "Events", emoji: "🏆", helper: "Meetups, tournaments, and cleanups" },
];

function normalizeCommunityBayId(value: any): CommunityBayId {
  const id = String(value || "").trim().toLowerCase();
  return COMMUNITY_BAYS.some((bay) => bay.id === id) ? (id as CommunityBayId) : "global";
}

function normalizeCommunityDockId(value: any): CommunityDockId {
  const id = String(value || "").trim().toLowerCase();
  return COMMUNITY_DOCKS.some((dock) => dock.id === id) ? (id as CommunityDockId) : "main";
}

function getCommunityBayMeta(id: any) {
  return COMMUNITY_BAYS.find((bay) => bay.id === normalizeCommunityBayId(id)) || COMMUNITY_BAYS[0];
}

function getCommunityDockMeta(id: any) {
  return COMMUNITY_DOCKS.find((dock) => dock.id === normalizeCommunityDockId(id)) || COMMUNITY_DOCKS[0];
}

function getPostBayId(post: any): CommunityBayId {
  return normalizeCommunityBayId(post?.bayId || post?.communityBayId || post?.bay);
}

function getPostDockId(post: any): CommunityDockId {
  return normalizeCommunityDockId(post?.dockId || post?.communityDockId || post?.roomId || post?.room);
}
type LikeMeta = {
  count: number;
  likedByMe: boolean;
  reaction?: CommunityReactionKey | null;
};

type CommunityReactionKey =
  | "like"
  | "love"
  | "care"
  | "laugh"
  | "wow"
  | "sad"
  | "angry";

const COMMUNITY_REACTIONS: Array<{
  key: CommunityReactionKey;
  label: string;
  emoji: string;
  tone: string;
}> = [
  { key: "like", label: "Like", emoji: "👍", tone: "bg-sky-500/18" },
  {
    key: "love",
    label: "Love",
    emoji: "❤️",
    tone: "bg-rose-500/18",
  },
  {
    key: "care",
    label: "Care",
    emoji: "🤗",
    tone: "bg-amber-400/18",
  },
  {
    key: "laugh",
    label: "Laugh",
    emoji: "😂",
    tone: "bg-yellow-400/18",
  },
  { key: "wow", label: "Wow", emoji: "😮", tone: "bg-violet-500/18" },
  {
    key: "sad",
    label: "Sad",
    emoji: "😢",
    tone: "bg-blue-500/18",
  },
  {
    key: "angry",
    label: "Angry",
    emoji: "😡",
    tone: "bg-red-500/18",
  },
];

const DEFAULT_COMMUNITY_REACTION = COMMUNITY_REACTIONS[0];

type CommunityPostPromptKey = "bite" | "water" | "setup" | "ask" | "catchNote";

const COMMUNITY_POST_PROMPTS: Array<{
  key: CommunityPostPromptKey;
  emoji: string;
  label: string;
  shortLabel: string;
  placeholder: string;
  helper: string;
}> = [
  {
    key: "bite",
    emoji: "🎣",
    label: "What's biting?",
    shortLabel: "Biting",
    placeholder:
      "What's biting today? Drop the species, time, depth, or quick clue…",
    helper: "Share a quick local bite signal.",
  },
  {
    key: "water",
    emoji: "🌊",
    label: "Water report",
    shortLabel: "Water",
    placeholder:
      "Drop a water report — clarity, temp, wind, current, weeds, pressure…",
    helper: "Help the Bay read the water.",
  },
  {
    key: "setup",
    emoji: "🧰",
    label: "Show setup",
    shortLabel: "Setup",
    placeholder:
      "Show the setup — rod, reel, line, lure, bait, rig, or why it worked…",
    helper: "Gear talk, rigs, and little tweaks.",
  },
  {
    key: "ask",
    emoji: "❓",
    label: "Ask the Bay",
    shortLabel: "Ask",
    placeholder:
      "Ask the Bay — species ID, lure choice, location strategy, gear help…",
    helper: "Get help from other anglers.",
  },
  {
    key: "catchNote",
    emoji: "📓",
    label: "Catch note",
    shortLabel: "Note",
    placeholder:
      "Drop a catch note — what happened, what changed, what you learned…",
    helper: "Turn one catch into a useful signal.",
  },
];

const DEFAULT_COMMUNITY_POST_PROMPT = COMMUNITY_POST_PROMPTS[0];

function getCommunityReactionByKey(key?: string | null) {
  return (
    COMMUNITY_REACTIONS.find((reaction) => reaction.key === key) ||
    DEFAULT_COMMUNITY_REACTION
  );
}

function normalizeCommunityReactionKey(
  value: any,
): CommunityReactionKey | null {
  const key = String(value || "")
    .trim()
    .toLowerCase();
  return COMMUNITY_REACTIONS.some((reaction) => reaction.key === key)
    ? (key as CommunityReactionKey)
    : null;
}

function BadgeIconRow({
  reputation,
  max = 3,
}: {
  reputation?: ReputationSummary | null;
  max?: number;
}) {
  const badges = getFeaturedBadges(reputation).slice(0, max);
  if (!badges.length) return null;

  return (
    <span className="ml-1.5 inline-flex items-center gap-1 align-middle">
      {badges.map((badge) => (
        <span
          key={badge.key}
          title={badge.label}
          aria-label={badge.label}
          className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-black/10 bg-[var(--community-surface-bg)] text-[11px] shadow-sm"
        >
          {badge.icon}
        </span>
      ))}
    </span>
  );
}

function resolveAuthorReputation(
  postOrComment: any,
  author: any,
): ReputationSummary | undefined {
  return (
    postOrComment?.authorReputation?.reputation ||
    postOrComment?.authorReputation ||
    author?.reputation
  );
}

function getFallbackProfileReputation(profile: any): ReputationSummary {
  return buildReputationSummary({
    species_mastery: Number(profile?.stats?.catches ?? profile?.fishCount ?? 0),
    explorer: Number(profile?.stats?.spots ?? profile?.watersFished ?? 0),
    community_contributor: 0,
    bazaar_trader: 0,
    tournament_competitor: 0,
  });
}

function useRoddoScrollGlow<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;

    const findScrollParent = (): HTMLElement | Window => {
      let el: HTMLElement | null = node.parentElement;
      while (el && el !== document.body) {
        const style = window.getComputedStyle(el);
        const canScroll = /(auto|scroll|overlay)/.test(style.overflowY);
        if (canScroll && el.scrollHeight > el.clientHeight) return el;
        el = el.parentElement;
      }
      return window;
    };

    const scroller = findScrollParent();
    let raf = 0;

    const readScroll = () => {
      if (scroller === window) {
        const doc = document.scrollingElement || document.documentElement;
        return {
          top: window.scrollY || doc.scrollTop || 0,
          max: Math.max(1, doc.scrollHeight - window.innerHeight),
        };
      }
      const el = scroller as HTMLElement;
      return {
        top: el.scrollTop,
        max: Math.max(1, el.scrollHeight - el.clientHeight),
      };
    };

    const update = () => {
      raf = 0;
      const { top, max } = readScroll();
      const progress = Math.max(0, Math.min(1, top / max));
      const eased = 1 - Math.pow(1 - progress, 2);
      const x = 48 + Math.sin(progress * Math.PI) * 8;
      const y = 12 + eased * 58;
      const opacity = 0.72 + progress * 0.18;

      node.style.setProperty("--roddo-scroll-glow-x", `${x.toFixed(1)}%`);
      node.style.setProperty("--roddo-scroll-glow-y", `${y.toFixed(1)}%`);
      node.style.setProperty("--roddo-scroll-glow-opacity", opacity.toFixed(2));
    };

    const requestUpdate = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(update);
    };

    update();
    const target = scroller === window ? window : (scroller as HTMLElement);
    target.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate, { passive: true });

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      target.removeEventListener("scroll", requestUpdate as EventListener);
      window.removeEventListener("resize", requestUpdate as EventListener);
    };
  }, []);

  return ref;
}

function getRoddoScrollGlowStyle(_isDarkMode: boolean): React.CSSProperties {
  return {
    backgroundImage: "none",
  };
}

const roddoTactilePolishStyles = `
  [data-roddo-tactile] button,
  [data-roddo-tactile] [role="button"],
  [data-roddo-tactile] a,
  [data-roddo-tactile] label[for],
  [data-roddo-tactile] select,
  [data-roddo-tactile] input,
  [data-roddo-tactile] textarea {
    -webkit-tap-highlight-color: transparent;
  }

  [data-roddo-tactile] button,
  [data-roddo-tactile] [role="button"],
  [data-roddo-tactile] a {
    transform: translateZ(0);
    transition:
      transform 180ms cubic-bezier(0.22, 1, 0.36, 1),
      filter 180ms cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 220ms cubic-bezier(0.22, 1, 0.36, 1),
      opacity 180ms cubic-bezier(0.22, 1, 0.36, 1),
      background 220ms cubic-bezier(0.22, 1, 0.36, 1),
      border-color 220ms cubic-bezier(0.22, 1, 0.36, 1);
    will-change: transform, filter;
  }

  [data-roddo-tactile] button:hover,
  [data-roddo-tactile] [role="button"]:hover,
  [data-roddo-tactile] a:hover {
    transform: translateY(-1px) translateZ(0);
    filter: brightness(1.035) saturate(1.035);
  }

  [data-roddo-tactile] button:active,
  [data-roddo-tactile] [role="button"]:active,
  [data-roddo-tactile] a:active {
    transform: translateY(1px) scale(0.985) translateZ(0);
    filter: brightness(0.985) saturate(0.98);
  }

  [data-roddo-tactile] button:focus-visible,
  [data-roddo-tactile] [role="button"]:focus-visible,
  [data-roddo-tactile] a:focus-visible,
  [data-roddo-tactile] input:focus-visible,
  [data-roddo-tactile] textarea:focus-visible,
  [data-roddo-tactile] select:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 3px color-mix(in srgb, var(--roddo-accent-plum-pop, #9278EC) 24%, transparent),
      0 0 20px color-mix(in srgb, var(--roddo-accent-plum-pop, #c4b5fd) 18%, transparent);
  }

  [data-roddo-tactile] .roddo-polish-card,
  [data-roddo-tactile] article {
    transition:
      transform 220ms cubic-bezier(0.22, 1, 0.36, 1),
      filter 220ms cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 260ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  [data-roddo-tactile] .roddo-polish-card:hover,
  [data-roddo-tactile] article:hover {
    transform: translateY(-1px);
    filter: saturate(1.025) brightness(1.015);
  }

  [data-roddo-tactile] .roddo-polish-energy {
    position: relative;
    isolation: isolate;
    overflow: hidden;
  }

  [data-roddo-tactile] .roddo-polish-energy::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    pointer-events: none;
    opacity: 0.72;
    background: rgba(146,120,236,0.42);
    transform: translateX(-18%);
    transition: opacity 220ms ease, transform 420ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  [data-roddo-tactile] .roddo-polish-energy:hover::before {
    opacity: 0.95;
    transform: translateX(0%);
  }

  [data-roddo-theme="light"] [data-roddo-tactile] .roddo-polish-energy::before,
  [data-roddo-tactile][data-roddo-theme="light"] .roddo-polish-energy::before {
    opacity: 0.50;
  }

  [data-roddo-tactile] .roddo-reaction-tray {
    transform-origin: left bottom;
    animation: roddoReactionPop 160ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  [data-roddo-tactile] .roddo-reaction-option {
    transition: transform 160ms cubic-bezier(0.22, 1, 0.36, 1), filter 160ms ease, box-shadow 160ms ease;
  }

  [data-roddo-tactile] .roddo-reaction-option:hover,
  [data-roddo-tactile] .roddo-reaction-option:focus-visible {
    transform: translateY(-5px) scale(1.08);
    filter: saturate(1.12) brightness(1.05);
  }

  @keyframes roddoReactionPop {
    from { opacity: 0; transform: translateY(8px) scale(0.94); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }

  @media (prefers-reduced-motion: reduce) {
    [data-roddo-tactile] button,
    [data-roddo-tactile] [role="button"],
    [data-roddo-tactile] a,
    [data-roddo-tactile] .roddo-polish-card,
    [data-roddo-tactile] article {
      transition: none !important;
      transform: none !important;
      filter: none !important;
    }
  }
`;

interface CommunityViewProps {
  currentUserProfile?: UserProfile;
  userProfile?: UserProfile;
  isDarkMode?: boolean;

  posts?: CommunityPost[];
  users?: Record<string, AppUser>;

  commentsByPost?: any;
  onCommentsOpened?: (postId: string) => void;
  followingIds?: string[];
  navigateTo?: any;

  requireJsAuth?: boolean;
  isJsAuthed?: boolean;

  isBootstrapped?: boolean;
  onRefreshFeed?: () => Promise<void> | void;

  focusPostId?: string;
  focusCommentId?: string;

  onLoadMorePosts?: () => Promise<void> | void;
  hasMorePosts?: boolean;
  isLoadingMorePosts?: boolean;

  onPostCreated?: (post: CommunityPost) => Promise<void> | void;
  onCreatePost?: (post: CommunityPost) => Promise<void> | void;

  onPostEdited?: (
    postId: string,
    updatedData: Partial<CommunityPost>,
  ) => Promise<void> | void;
  onEditPost?: (
    postId: string,
    updatedData: Partial<CommunityPost>,
  ) => Promise<void> | void;

  onPostDeleted?: (postId: string) => Promise<void> | void;
  onDeletePost?: (postId: string) => Promise<void> | void;

  onCommentAdded?: (postId: string, comment: Comment) => Promise<void> | void;
  onAddComment?: (postId: string, comment: Comment) => Promise<void> | void;

  onCommentEdited?: (
    postId: string,
    commentId: string,
    newText: string,
  ) => Promise<void> | void;
  onEditComment?: (
    postId: string,
    commentId: string,
    newText: string,
  ) => Promise<void> | void;

  onCommentDeleted?: (
    postId: string,
    commentId: string,
  ) => Promise<void> | void;
  onDeleteComment?: (postId: string, commentId: string) => Promise<void> | void;

  onToggleLike: (
    postId: string,
    reaction?: CommunityReactionKey,
  ) => Promise<void> | void;
  onToggleCommentLike: (
    postId: string,
    commentId: string,
    reaction?: CommunityReactionKey,
  ) => Promise<void> | void;

  onAddPostToDiary?: (post: CommunityPost) => Promise<void> | void;
  onAddToCatchDiary?: (post: CommunityPost) => Promise<void> | void;
  onAddToCatchDiaryLegacy?: (post: CommunityPost) => Promise<void> | void;

  onOpenUserProfile?: (userId: string) => void;

  viewState?: {
    targetId?: string;
    subTargetId?: string;
    focusPostId?: string;
    focusCommentId?: string;
    openComposer?: boolean;
    feedUserId?: string;
    tab?: CommunityTabId | "crew";
    openCrew?: boolean;
  };

  addToast: (message: string, type?: ToastType) => void;
}

function timeAgo(ts?: number) {
  if (!ts) return "";
  const diff = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function normalizeMentionToken(value: unknown): string {
  return String(value || "")
    .trim()
    .replace(/^@+/, "")
    .toLowerCase();
}

function renderMentionRichText(
  text: string,
  mentions: CommunityMention[] | undefined,
  onOpenUserProfile?: (userId: string) => void,
) {
  const mentionMap = new Map<string, CommunityMention>();
  (mentions || []).forEach((m) => {
    const key = normalizeMentionToken((m as any)?.username);
    if (key) mentionMap.set(key, m);
  });

  return String(text || "")
    .split(/(@[a-zA-Z0-9_.-]+)/g)
    .map((part, index) => {
      if (!part.startsWith("@")) {
        return <React.Fragment key={`txt-${index}`}>{part}</React.Fragment>;
      }

      const key = normalizeMentionToken(part);
      const hit = mentionMap.get(key);

      if (!hit || !hit.uid) {
        return (
          <span
            key={`mention-${index}`}
            className="font-extrabold text-[#ffcd64]"
          >
            {part}
          </span>
        );
      }

      const openMention = () => onOpenUserProfile?.(hit.uid);

      return (
        <span
          key={`mention-${index}`}
          role="link"
          tabIndex={0}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openMention();
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              openMention();
            }
          }}
          className="inline cursor-pointer rounded-md px-0.5 font-extrabold text-[#ffcd64] underline decoration-[#ffcd64]/55 underline-offset-2 transition hover:opacity-85 active:opacity-70"
          title={`Open @${hit.username || part.replace(/^@/, "")}`}
        >
          {part}
        </span>
      );
    });
}

function canReadPostClient(post: CommunityPost, uid: string) {
  const p: any = post as any;
  const vis = typeof p.visibility === "string" ? p.visibility : "public";
  if (vis !== "private") return true;
  const authorId = String(p.authorId || p.uid || "");
  return authorId === uid;
}

const txtSub = txt.sub;

const glassBase =
  "relative overflow-hidden rounded-[20px] border isolate";
const glassLight =
  "bg-[var(--roddo-panel-light)] border-[var(--roddo-border-light)] shadow-[0_10px_26px_rgba(89,65,37,0.08)]";
const glassDark =
  "dark:bg-[var(--roddo-panel-dark)] dark:border-[var(--roddo-border-dark)] dark:shadow-[0_12px_30px_rgba(3,0,16,0.28)]";
const glass = `${glassBase} ${glassLight} ${glassDark}`;

const glassSheen = "";

const LIGHT_PILL_BG = "rgba(124,111,247,0.14)";
const LIGHT_PILL_TXT = "#4b44a8";
const DARK_PILL_BG = "rgba(146,120,236,0.11)";
const DARK_PILL_TXT = "#E1D8FF";

const inputShell =
  "border  placeholder:text-[var(--community-text-secondary)] transition-[border-color,box-shadow,background] focus:border-[#9278EC]/55 focus:ring-2 focus:ring-[#7657E8]/15 dark:focus:border-[#9278EC]/55 dark:focus:ring-[#7657E8]/18";

const dangerLink = "text-[#ff4d6d] hover:underline";
const subtleLink = "text-[var(--community-text-secondary)] hover:underline";

const flatCardBase =
  "relative isolate overflow-hidden rounded-[18px] border " +
  "border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)] " +
  "bg-[var(--roddo-panel-light)] text-[var(--roddo-text-light)] dark:bg-[var(--roddo-panel-dark)] dark:text-[var(--roddo-text-dark)] " +
  "shadow-[0_5px_14px_rgba(89,65,37,0.055)] dark:shadow-[0_7px_18px_rgba(3,0,16,0.18)] " +
  "";
const flatCardInner = `${flatCardBase} px-4 py-4`;

const innerPanelClass =
  "relative overflow-hidden rounded-[16px] border border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)] " +
  "bg-[var(--roddo-panel-light-strong)] text-[var(--roddo-text-light)] dark:bg-[var(--roddo-panel-dark-strong)] dark:text-[var(--roddo-text-dark)]  " +
  "shadow-[0_6px_16px_rgba(89,65,37,0.06)] dark:shadow-[0_8px_20px_rgba(3,0,16,0.20)] " +
  `${glassSheen} px-3 py-3`;

const chipBase =
  "shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[11px] font-extrabold " +
  "border border-[var(--roddo-border-light)] bg-[var(--roddo-panel-light-strong)] text-[var(--roddo-text-light)] " +
  "dark:border-[var(--roddo-border-dark)] dark:bg-[var(--roddo-panel-dark-strong)] dark:text-[var(--roddo-text-dark)] " +
  "shadow-[0_4px_12px_rgba(89,65,37,0.06)] " +
  " transition-transform hover:-translate-y-0.5 active:translate-y-0";

const chipOn =
  "ring-2 ring-[#ffcd64]/50 bg-[var(--roddo-panel-light-strong)] dark:ring-[#9278EC]/45 dark:bg-[var(--roddo-panel-dark-strong)] " +
  "shadow-[0_8px_18px_rgba(15,23,42,0.14)]";

const softPill =
  "inline-flex items-center rounded-full border border-[var(--roddo-border-light)] px-3 py-1.5 text-xs font-semibold text-[var(--roddo-text-light)] dark:border-[var(--roddo-border-dark)] dark:text-[var(--roddo-text-dark)] " +
  "shadow-[0_10px_22px_rgba(15,23,42,0.10)]";

const softBtn =
  "inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-xs font-semibold " +
  "border border-[var(--roddo-border-light)] bg-[var(--roddo-panel-light)] text-[var(--roddo-text-light)] " +
  "dark:border-[var(--roddo-border-dark)] dark:bg-[var(--roddo-panel-dark)] dark:text-[var(--roddo-text-dark)] " +
  "shadow-[0_10px_22px_rgba(15,23,42,0.10)] " +
  "hover:brightness-[1.03] transition";

const softBtnGhost =
  "inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-xs font-semibold " +
  "border border-[var(--roddo-border-light)] bg-transparent text-[var(--roddo-text-light-soft)] " +
  "dark:border-[var(--roddo-border-dark)] dark:text-[var(--roddo-text-dark-soft)] " +
  "hover:opacity-90 transition";

const yellowBtn =
  "inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-xs font-extrabold " +
  "bg-[#ffcd64] text-[#2d2b42] border border-white/20 " +
  "shadow-[0_12px_28px_rgba(15,23,42,0.16)] " +
  "hover:-translate-y-0.5 active:translate-y-0 transition-transform";

const RESP_CARD =
  "rounded-[13px] border border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)] " +
  "bg-[var(--roddo-panel-light)] text-[var(--roddo-text-light)] dark:bg-[var(--roddo-panel-dark)] dark:text-[var(--roddo-text-dark)] " +
  "px-3 py-2.5";

const RESP_USER =
  "text-xs font-semibold text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)] flex items-center gap-1";
const RESP_TEXT =
  "text-sm text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)] leading-snug";
const RESP_TIME =
  "text-[11px] text-[var(--roddo-text-light-muted)] dark:text-[var(--roddo-text-dark-muted)]";

const RESP_ACTION_ROW = "mt-1 flex flex-wrap items-center gap-2 text-xs";
const RESP_ACTION_LIKE_REPLY =
  "inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-white/7 text-[13px] font-black text-[var(--roddo-text-light-soft)] shadow-sm hover:-translate-y-0.5 hover:bg-white/12 dark:text-[var(--roddo-text-dark-soft)]";

const RESP_LIKE_BTN = (liked: boolean, disabled?: boolean) =>
  [
    "inline-flex h-7 min-w-7 items-center justify-center gap-1 rounded-full border border-white/10 px-2 text-[13px] font-black shadow-sm hover:-translate-y-0.5",
    liked
      ? "bg-[#ff4d6d]/18 text-[#ff7b95] border-[#ff7b95]/35"
      : "bg-white/7 text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]",
    disabled ? "opacity-60 cursor-not-allowed" : "",
  ].join(" ");

type MiniMenuProps = {
  onEdit?: () => void;
  onDelete?: () => void;
  disabled?: boolean;
};

function MiniMenu({ onEdit, onDelete, disabled }: MiniMenuProps) {
  const portalTarget = useRoutePortalTarget();
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const hasAny = Boolean(onEdit) || Boolean(onDelete);
  if (!hasAny) return null;

  const measure = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      top: Math.round(r.bottom + 8),
      left: Math.round(r.right),
    });
  };

  useEffect(() => {
    if (!open) return;
    measure();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };

    const onScrollOrResize = () => measure();

    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);

    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`${subtleLink} font-extrabold leading-none px-2 py-1 rounded-full hover:opacity-90 ${
          disabled ? "opacity-50 pointer-events-none" : ""
        }`}
        aria-label="More"
        title="More"
      >
        ⋯
      </button>

      {open && portalTarget &&
        createPortal(
          <div
            ref={menuRef}
            className="
              fixed z-[99999]
              min-w-[160px] overflow-hidden rounded-[14px]
              bg-[#FFFCF7] border border-black/10
              shadow-[0_18px_55px_rgba(15,23,42,0.22)]
              dark:bg-[#101827] dark:border-white/14 dark:shadow-[0_22px_60px_rgba(0,0,0,0.70)]
            "
            style={{
              top: `min(${pos.top}px, calc(100dvh - var(--roddo-nav-h, 84px) - 120px))`,
              left: pos.left,
              transform: "translateX(-100%)",
            }}
            role="menu"
          >
            {onEdit && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onEdit();
                }}
                className="
                  w-full text-left px-4 py-3 text-sm font-semibold
                  text-[var(--community-text-primary)] hover:bg-black/5 dark:hover:bg-white/10
                "
                role="menuitem"
              >
                Edit
              </button>
            )}

            {onDelete && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onDelete();
                }}
                className="
                  w-full text-left px-4 py-3 text-sm font-semibold
                  text-[#ff4d6d] hover:bg-black/5
                  dark:hover:bg-black/10
                "
                role="menuitem"
              >
                Delete
              </button>
            )}
          </div>,
          portalTarget,
        )}
    </>
  );
}

const DEFAULT_AVATAR = "/avatar-default.png";

const pickAvatarUrl = (...candidates: any[]) => {
  for (const c of candidates) {
    const s = typeof c === "string" ? c.trim() : "";
    if (!s) continue;
    if (s.startsWith("gs://")) continue;
    return s;
  }
  return DEFAULT_AVATAR;
};

function CameraIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M8.2 7.2 9.1 5.7c.2-.4.6-.7 1.1-.7h3.6c.5 0 .9.3 1.1.7l.9 1.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M7 7h10c1.7 0 3 1.3 3 3v7c0 1.7-1.3 3-3 3H7c-1.7 0-3-1.3-3-3v-7c0-1.7 1.3-3 3-3Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M12 17a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  );
}

function BazaarStyleInlineGallery({
  images,
  title,
  onOpen,
}: {
  images: string[];
  title?: string;
  onOpen: (startIndex: number) => void;
}) {
  const safe = Array.isArray(images) ? images.filter(Boolean) : [];
  const [active, setActive] = useState(0);

  useEffect(() => setActive(0), [safe.length]);

  if (!safe.length) return null;
  const main = safe[Math.min(active, safe.length - 1)];

  return (
    <div className="overflow-hidden rounded-[16px] border border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)]">
      <button
        type="button"
        onClick={() => onOpen(active)}
        className="relative block w-full cursor-zoom-in"
        title="Tap to view"
      >
        <div className="relative w-full pb-[66%] bg-black/5 flex items-center justify-center">
          <img
            src={main}
            alt={title || "Post image"}
            className="absolute inset-0 w-full h-full object-contain"
            loading="lazy"
          />
        </div>

        {safe.length > 1 && (
          <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-semibold">
            {active + 1}/{safe.length}
          </div>
        )}
      </button>

      {safe.length > 1 && (
        <div className="relative z-10 p-3">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {safe.slice(0, 10).map((u, idx) => (
              <button
                key={`${u}-${idx}`}
                type="button"
                onClick={() => setActive(idx)}
                className={`shrink-0 w-16 h-16 rounded-2xl overflow-hidden border transition ${
                  idx === active
                    ? "border-[#ffcd64] ring-2 ring-[#ffcd64]/40"
                    : "border-black/10 hover:border-black/20"
                }`}
                title={`View ${idx + 1}`}
              >
                <img
                  src={u}
                  alt={`thumb-${idx + 1}`}
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </button>
            ))}

            {safe.length > 10 && (
              <button
                type="button"
                onClick={() => onOpen(active)}
                className="shrink-0 h-16 px-3 rounded-2xl bg-[var(--community-surface-bg)] border-2 border-black/10 text-[11px] font-semibold text-[var(--community-text-secondary)] hover:opacity-90"
                title="View all"
              >
                +{safe.length - 10} more
              </button>
            )}
          </div>

          <div className="mt-2 flex items-center justify-between">
            <div className="text-xs text-[var(--roddo-text-light-muted)] dark:text-[var(--roddo-text-dark-muted)]">
              {safe.length} photos
            </div>
            <button
              type="button"
              onClick={() => onOpen(active)}
              className="text-xs font-semibold text-[var(--community-text-secondary)] hover:underline"
            >
              View all
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function getPostImages(post: CommunityPost): string[] {
  const p: any = post as any;
  if (Array.isArray(p.images) && p.images.length)
    return p.images.filter(Boolean);
  if (Array.isArray(p.imageUrls) && p.imageUrls.length)
    return p.imageUrls.filter(Boolean);

  const legacy = p.imageUrl ?? p.image ?? null;
  if (typeof legacy === "string" && legacy) return [legacy];
  return [];
}

function parseCatchCaption(caption?: string): {
  species?: string;
  weight?: string;
  location?: string;
} {
  const c = String(caption || "").trim();
  if (!c) return {};
  const m = c.match(/^(.+?)\s*[–—-]\s*(.+?)\s*@\s*(.+)$/);
  if (!m) return {};
  return {
    species: m[1]?.trim() || undefined,
    weight: m[2]?.trim() || undefined,
    location: m[3]?.trim() || undefined,
  };
}

function buildCatchMetaPill(post: CommunityPost): string | null {
  const p: any = post as any;

  const caption = String(p.caption ?? p.text ?? "").trim();
  const parsed = parseCatchCaption(caption);

  const species = String(p.species || parsed.species || "").trim();
  const location = String(p.location || parsed.location || "").trim();
  const weight = String((p as any).weight || parsed.weight || "").trim();

  const rod = String(p.rod || "").trim();
  const reel = String(p.reel || "").trim();
  const lb = String(p.lureOrBait || "").trim();
  const lbType = String(p.lureOrBaitType || "").trim();

  const pieces: string[] = [];

  const fishBits = [species, weight, location].filter(Boolean);
  if (fishBits.length) pieces.push(fishBits.join(" • "));

  const gearBits: string[] = [];
  if (rod) gearBits.push(`Rod: ${rod}`);
  if (reel) gearBits.push(`Reel: ${reel}`);
  if (lb) gearBits.push(`${lbType ? `${lbType}: ` : "Lure/Bait: "}${lb}`);
  if (gearBits.length) pieces.push(gearBits.join(" • "));

  const pill = pieces.join("  —  ").trim();
  return pill ? pill : null;
}

function getCommunityPostMood(post: CommunityPost): {
  label: string;
  icon: string;
  tone: "catch" | "photo" | "story";
} {
  const p: any = post as any;
  const caption = String(p.caption ?? p.text ?? "").trim();
  const parsed = parseCatchCaption(caption);
  const hasCatchData = Boolean(
    String(p.species || parsed.species || "").trim() ||
    String(p.location || parsed.location || "").trim() ||
    String((p as any).weight || parsed.weight || "").trim() ||
    String(p.rod || "").trim() ||
    String(p.reel || "").trim() ||
    String(p.lureOrBait || "").trim(),
  );

  if (hasCatchData) return { label: "Catch moment", icon: "🐟", tone: "catch" };
  if (getPostImages(post).length)
    return { label: "Water memory", icon: "📸", tone: "photo" };
  return { label: "Dock talk", icon: "🌊", tone: "story" };
}

function getCommunityPostMoodStyle(
  tone: "catch" | "photo" | "story",
  isDarkMode: boolean,
): React.CSSProperties {
  if (tone === "catch") {
    return {
      background: isDarkMode
        ? "rgba(255,205,100,0.16)"
        : "rgba(255,205,100,0.28)",
      color: isDarkMode ? "#fff3c9" : "#5e4310",
      borderColor: isDarkMode
        ? "rgba(255,205,100,0.30)"
        : "rgba(160,112,22,0.16)",
    };
  }

  if (tone === "photo") {
    return {
      background: isDarkMode
        ? "rgba(103,232,249,0.12)"
        : "rgba(103,232,249,0.20)",
      color: isDarkMode ? "#d9fbff" : "#175263",
      borderColor: isDarkMode
        ? "rgba(103,232,249,0.22)"
        : "rgba(23,82,99,0.12)",
    };
  }

  return {
    background: isDarkMode
      ? "rgba(112,87,232,0.10)"
      : "rgba(196,181,253,0.18)",
    color: isDarkMode ? "#E2DCFF" : "#4d3e8f",
    borderColor: isDarkMode ? "rgba(112,87,232,0.22)" : "rgba(77,62,143,0.12)",
  };
}

function getCommunityStoryCardStyle(
  tone: "catch" | "photo" | "story",
  isDarkMode: boolean,
): React.CSSProperties {
  const borderColor =
    tone === "catch"
      ? (isDarkMode ? "rgba(255,205,100,0.22)" : "rgba(160,112,22,0.16)")
      : tone === "photo"
        ? (isDarkMode ? "rgba(103,232,249,0.18)" : "rgba(23,82,99,0.12)")
        : (isDarkMode ? "rgba(112,87,232,0.18)" : "rgba(77,62,143,0.10)");
  return {
    background: "transparent",
    borderLeft: `2px solid ${borderColor}`,
  };
}

function getCommunityStoryLine(
  mood: { label: string; tone: "catch" | "photo" | "story" },
  commentCount: number,
  likeCount: number,
) {
  if (mood.tone === "catch") return "A fresh read from the water";
  if (mood.tone === "photo")
    return commentCount || likeCount
      ? "A little scene from Big Blue"
      : "A quiet frame from the water";
  return commentCount || likeCount
    ? "The dock is talking"
    : "A note floated into Baleen Bay";
}

function cleanBodyTextWhenMetaPillIsShown(raw?: string): string {
  const text = String(raw || "").trim();
  if (!text) return "";

  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  const kept: string[] = [];

  for (const line of lines) {
    if (/^(rod|reel|bait|lure\/bait|lure)\s*:/i.test(line)) continue;
    const parsed = parseCatchCaption(line);
    if (parsed.species && parsed.weight && parsed.location) continue;
    kept.push(line);
  }

  return kept.join("\n").trim();
}

function GalleryLightbox({
  images,
  startIndex,
  title,
  onClose,
}: {
  images: string[];
  startIndex: number;
  title?: string;
  onClose: () => void;
}) {
  const portalTarget = useRoutePortalTarget();
  const safeImages = Array.isArray(images) ? images.filter(Boolean) : [];
  const [idx, setIdx] = useState(() => {
    const n = Number(startIndex);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(safeImages.length - 1, n));
  });

  useEffect(() => {
    setIdx((prev) => Math.max(0, Math.min(safeImages.length - 1, prev)));
  }, [safeImages.length]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft")
        setIdx((v) => (v - 1 + safeImages.length) % safeImages.length);
      if (e.key === "ArrowRight") setIdx((v) => (v + 1) % safeImages.length);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose, safeImages.length]);

  const src = safeImages[idx];

  if (!portalTarget) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] bg-black/85 backdrop-blur-sm grid place-items-center p-3"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
    >
      <div
        className="relative w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-2 -right-2 z-10 w-11 h-11 rounded-full bg-white/10 border border-white/15 text-white grid place-items-center shadow-[0_12px_30px_rgba(0,0,0,0.35)] hover:bg-white/15"
          aria-label="Close"
          title="Close"
        >
          <XIcon className="w-5 h-5" />
        </button>

        <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-black/30">
          {src ? (
            <img
              src={src}
              alt={title || "Expanded"}
              className="w-full max-h-[80vh] object-contain cursor-zoom-out"
              onClick={onClose}
            />
          ) : null}
        </div>

        {safeImages.length > 1 && (
          <div className="mt-3 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="text-xs text-white/70">
                {idx + 1} / {safeImages.length}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setIdx(
                      (v) => (v - 1 + safeImages.length) % safeImages.length,
                    )
                  }
                  className="rounded-full px-3 py-1.5 text-xs font-semibold bg-white/10 border border-white/15 text-white hover:bg-white/15"
                >
                  ← Prev
                </button>
                <button
                  type="button"
                  onClick={() => setIdx((v) => (v + 1) % safeImages.length)}
                  className="rounded-full px-3 py-1.5 text-xs font-semibold bg-white/10 border border-white/15 text-white hover:bg-white/15"
                >
                  Next →
                </button>
              </div>
            </div>

            <div className="flex gap-2 overflow-x-auto pb-1">
              {safeImages.map((u, i) => (
                <button
                  key={`${u}-${i}`}
                  type="button"
                  onClick={() => setIdx(i)}
                  className={`shrink-0 w-16 h-16 rounded-2xl overflow-hidden border transition ${
                    i === idx
                      ? "border-[#ffcd64] ring-2 ring-[#ffcd64]/40"
                      : "border-white/15 hover:border-white/30"
                  }`}
                  title={`View ${i + 1}`}
                >
                  <img
                    src={u}
                    alt={`thumb-${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>,
    portalTarget,
  );
}

type PostMenuProps = {
  onEdit?: () => void;
  onDelete?: () => void;
  extraItems?: React.ReactNode;
};

function PostMenu({ onEdit, onDelete, extraItems }: PostMenuProps) {
  const portalTarget = useRoutePortalTarget();
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 0,
  });

  const hasAny = Boolean(extraItems) || Boolean(onEdit) || Boolean(onDelete);
  if (!hasAny) return null;

  const measure = () => {
    const el = btnRef.current;
    if (!el) return;

    const r = el.getBoundingClientRect();
    const headerHeightVar = Number(
      getComputedStyle(document.documentElement)
        .getPropertyValue("--roddo-header-h")
        .replace("px", "")
        .trim(),
    );
    const headerBottom =
      Math.max(Number.isFinite(headerHeightVar) ? headerHeightVar : 0, 96) + 8;
    const desiredTop = Math.round(r.bottom + 8);

    setPos({
      top: Math.max(desiredTop, headerBottom),
      left: Math.round(r.right),
      width: Math.round(r.width),
    });
  };

  useEffect(() => {
    if (!open) return;
    measure();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };

    const onScrollOrResize = () => {
      measure();
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);

    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="
          h-10 w-10 grid place-items-center rounded-full
          bg-transparent hover:opacity-90
          text-[var(--community-text-primary)]
          text-xl leading-none font-black
          transition hover:-translate-y-0.5 active:translate-y-0
        "
        aria-label="More"
        title="More"
      >
        ⋯
      </button>

      {open && portalTarget &&
        createPortal(
          <div
            ref={menuRef}
            className="
              fixed z-[99999]
              w-56 overflow-hidden rounded-[14px]
              bg-[#FFFCF7] border border-black/10
              shadow-[0_18px_55px_rgba(15,23,42,0.22)]
              dark:bg-[#101827] dark:border-white/14 dark:shadow-[0_22px_60px_rgba(0,0,0,0.70)]
            "
            style={{
              top: `min(${pos.top}px, calc(100dvh - var(--roddo-nav-h, 84px) - 190px))`,
              left: pos.left,
              transform: "translateX(-100%)",
            }}
            role="menu"
          >
            {extraItems}

            {onEdit && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onEdit();
                }}
                className="
                  w-full text-left px-4 py-3 text-sm font-semibold
                  text-[var(--community-text-primary)] hover:bg-black/5 dark:text-[var(--community-text-primary)] dark:hover:bg-white/10
                "
                role="menuitem"
              >
                ✏️ Edit
              </button>
            )}

            {onDelete && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onDelete();
                }}
                className="
                  w-full text-left px-4 py-3 text-sm font-semibold
                  text-[#ff4d6d] hover:bg-black/5
                  dark:hover:bg-black/5
                "
                role="menuitem"
              >
                🗑️ Delete
              </button>
            )}
          </div>,
          portalTarget,
        )}
    </>
  );
}

type CommunityReportModalProps = {
  target: { userId: string; userName: string; postId?: string | null };
  reason: UserReportReason;
  details: string;
  isSubmitting: boolean;
  onReasonChange: (reason: UserReportReason) => void;
  onDetailsChange: (value: string) => void;
  onCancel: () => void;
  onSubmit: () => void | Promise<void>;
};

const REPORT_REASON_OPTIONS: Array<{
  value: UserReportReason;
  label: string;
  helper: string;
}> = [
  {
    value: "spam",
    label: "Spam",
    helper: "Repeated, low-quality, or unrelated content.",
  },
  {
    value: "harassment",
    label: "Harassment",
    helper: "Bullying, threats, or targeted abuse.",
  },
  {
    value: "impersonation",
    label: "Impersonation",
    helper: "Pretending to be someone else.",
  },
  {
    value: "inappropriate_content",
    label: "Inappropriate content",
    helper: "Unsafe, explicit, hateful, or rule-breaking content.",
  },
  {
    value: "scam",
    label: "Scam",
    helper: "Fraud, phishing, suspicious selling, or deceptive behavior.",
  },
  {
    value: "other",
    label: "Other",
    helper: "Something else admins should review.",
  },
];

function CommunityReportModal({
  target,
  reason,
  details,
  isSubmitting,
  onReasonChange,
  onDetailsChange,
  onCancel,
  onSubmit,
}: CommunityReportModalProps) {
  const portalTarget = useRoutePortalTarget();
  const detailsRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isSubmitting) onCancel();
    };

    window.addEventListener("keydown", onKeyDown);
    const focusTimer = window.setTimeout(() => detailsRef.current?.focus(), 80);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isSubmitting, onCancel]);

  const cleanDetails = details.trim();
  const canSubmit =
    Boolean(target.userId) &&
    Boolean(reason) &&
    cleanDetails.length >= 6 &&
    !isSubmitting;

  if (!portalTarget) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] grid place-items-center bg-black/55 px-4 py-5 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="community-report-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSubmitting) onCancel();
      }}
    >
      <div className="w-full max-w-md overflow-hidden rounded-[22px] border border-white/40 bg-[var(--community-card-bg)] text-[var(--community-text-primary)] shadow-[0_24px_80px_rgba(15,23,42,0.35)] dark:border-white/14 dark:shadow-[0_28px_90px_rgba(0,0,0,0.70)]">
        <div className="relative border-b border-black/10 bg-[#FFF8ED] px-5 py-4 dark:border-white/10 dark:bg-[#101827]">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full border border-black/10 bg-white/65 text-[var(--community-text-primary)] shadow-sm transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/12 dark:bg-white/10"
            aria-label="Close report dialog"
            title="Close"
          >
            <XIcon className="h-4 w-4" />
          </button>

          <div className="pr-10">
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#ff4d6d]">
              Community safety
            </p>
            <h2
              id="community-report-title"
              className="mt-1 text-xl font-black leading-tight"
            >
              Report this post
            </h2>
            <p className="mt-1 text-sm font-semibold text-[var(--community-text-secondary)]">
              Tell the admins what looks wrong with{" "}
              {target.userName || "this user"}’s post.
            </p>
          </div>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div>
            <label className="text-xs font-black uppercase tracking-[0.12em] text-[var(--community-text-secondary)]">
              What’s the issue?
            </label>
            <div className="mt-2 grid grid-cols-1 gap-2">
              {REPORT_REASON_OPTIONS.map((option) => {
                const selected = reason === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => onReasonChange(option.value)}
                    disabled={isSubmitting}
                    className={`rounded-[15px] border px-3.5 py-3 text-left transition ${
                      selected
                        ? "border-[#ff4d6d]/55 bg-[#ff4d6d]/10 shadow-[0_10px_24px_rgba(255,77,109,0.14)]"
                        : "border-black/10 bg-white/45 hover:bg-white/70 dark:border-white/10 dark:bg-white/6 dark:hover:bg-white/10"
                    } ${isSubmitting ? "cursor-not-allowed opacity-70" : ""}`}
                  >
                    <span className="flex items-center justify-between gap-3">
                      <span className="text-sm font-black">{option.label}</span>
                      <span
                        className={`grid h-5 w-5 place-items-center rounded-full border text-[10px] ${selected ? "border-[#ff4d6d] bg-[#ff4d6d] text-white" : "border-black/15 dark:border-white/18"}`}
                      >
                        {selected ? "✓" : ""}
                      </span>
                    </span>
                    <span className="mt-0.5 block text-xs font-semibold text-[var(--community-text-secondary)]">
                      {option.helper}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label
              htmlFor="community-report-details"
              className="text-xs font-black uppercase tracking-[0.12em] text-[var(--community-text-secondary)]"
            >
              Notes for admins
            </label>
            <textarea
              id="community-report-details"
              ref={detailsRef}
              value={details}
              onChange={(event) =>
                onDetailsChange(event.target.value.slice(0, 800))
              }
              disabled={isSubmitting}
              rows={3}
              placeholder="Briefly describe what happened so admins can review it."
              className="mt-2 w-full resize-none rounded-[15px] border border-black/10 bg-white/70 px-4 py-3 text-sm font-semibold text-[var(--community-text-primary)] outline-none transition placeholder:text-[var(--community-text-secondary)]/70 focus:border-[#ffcd64] focus:ring-4 focus:ring-[#ffcd64]/20 disabled:cursor-not-allowed disabled:opacity-70 dark:border-white/10 dark:bg-white/7"
            />
            <div className="mt-1 flex items-center justify-between gap-2 text-[11px] font-semibold text-[var(--community-text-secondary)]">
              <span>
                {cleanDetails.length < 6
                  ? "Add a few details before submitting."
                  : "Thanks — this helps admins review faster."}
              </span>
              <span>{details.length}/800</span>
            </div>
          </div>

          <div className="rounded-2xl border border-black/10 bg-black/[0.03] px-3 py-2 text-xs font-semibold text-[var(--community-text-secondary)] dark:border-white/10 dark:bg-white/[0.04]">
            Reports go to the admin review queue. The reported user is not
            notified by this form.
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-black/10 px-5 py-4 dark:border-white/10">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="rounded-full border-2 border-black/10 bg-white/55 px-4 py-2 text-sm font-black text-[var(--community-text-primary)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/12 dark:bg-white/8"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className="rounded-full bg-[#ff4d6d] px-4 py-2 text-sm font-black text-white shadow-[0_14px_30px_rgba(255,77,109,0.24)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? "Submitting…" : "Submit report"}
          </button>
        </div>
      </div>
    </div>,
    portalTarget,
  );
}


type FastComposerTextAreaProps = {
  seed: string;
  seedVersion: number;
  placeholder: string;
  inputBg: string;
  color: string;
  borderColor: string;
  onTextRefChange: (value: string) => void;
  onPresenceChange: (hasText: boolean) => void;
  onDropFiles: (files: FileList | null) => void;
};

function FastComposerTextArea({
  seed,
  seedVersion,
  placeholder,
  inputBg,
  color,
  borderColor,
  onTextRefChange,
  onPresenceChange,
  onDropFiles,
}: FastComposerTextAreaProps) {
  const [value, setValue] = useState(seed);
  const hasTextRef = useRef(Boolean(seed.trim()));

  useEffect(() => {
    setValue(seed);
    onTextRefChange(seed);
    const nextHasText = Boolean(seed.trim());
    hasTextRef.current = nextHasText;
    onPresenceChange(nextHasText);
  }, [seed, seedVersion, onTextRefChange, onPresenceChange]);

  const handleChange = (next: string) => {
    setValue(next);
    onTextRefChange(next);
    const nextHasText = Boolean(next.trim());
    if (nextHasText !== hasTextRef.current) {
      hasTextRef.current = nextHasText;
      onPresenceChange(nextHasText);
    }
  };

  return (
    <textarea
      rows={4}
      value={value}
      onChange={(e) => handleChange(e.target.value)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        onDropFiles(e.dataTransfer.files);
      }}
      className="w-full resize-none rounded-[15px] border px-4 py-3 text-[15px] leading-relaxed placeholder:text-[var(--community-text-secondary)] focus:outline-none focus:ring-2 focus:ring-[#7657E8]/24"
      style={{ background: inputBg, color, borderColor }}
      placeholder={placeholder}
    />
  );
}

type FastCommentComposerProps = {
  postId: string;
  seed: string;
  pending: boolean;
  mentionableUsers: any[];
  placeholder: string;
  sendLabel: string;
  sendingLabel: string;
  panelBg: string;
  textColor: string;
  secondaryColor: string;
  borderColor: string;
  onSubmit: (text: string) => Promise<boolean>;
};

function FastCommentComposer({
  postId,
  seed,
  pending,
  mentionableUsers,
  placeholder,
  sendLabel,
  sendingLabel,
  panelBg,
  textColor,
  secondaryColor,
  borderColor,
  onSubmit,
}: FastCommentComposerProps) {
  const [value, setValue] = useState(seed);

  useEffect(() => {
    setValue(seed);
  }, [seed]);

  const suggestions = useMemo(() => {
    const match = String(value || "").match(/(?:^|\s)@([a-zA-Z0-9_.-]{1,32})$/);
    if (!match) return [];
    const needle = normalizeMentionToken(match[1]);
    const source = Array.isArray(mentionableUsers) ? mentionableUsers : [];
    if (!needle) return source.slice(0, 6);
    return source
      .filter((u: any) => {
        const username = normalizeMentionToken(u?.username);
        const display = normalizeMentionToken(u?.displayName);
        return username.startsWith(needle) || display.startsWith(needle);
      })
      .slice(0, 6);
  }, [value, mentionableUsers]);

  const insertMention = (username: string) => {
    const next = String(value || "").replace(
      /(^|\s)@([a-zA-Z0-9_.-]{1,32})$/,
      `$1@${normalizeMentionToken(username)} `,
    );
    setValue(next);
  };

  const submit = async () => {
    const clean = value.trim();
    if (!clean || pending) return;
    const ok = await onSubmit(clean);
    if (ok) setValue("");
  };

  return (
    <div className="w-full space-y-2 pt-1">
      {suggestions.length > 0 && (
        <div
          className="overflow-hidden rounded-[22px] border p-2 shadow-[0_10px_24px_rgba(15,23,42,0.18)]"
          style={{ background: panelBg, borderColor }}
        >
          <div
            className="px-2 pb-1 text-[10px] font-black uppercase tracking-[0.16em]"
            style={{ color: secondaryColor }}
          >
            Mention someone
          </div>
          <div className="space-y-1">
            {suggestions.map((suggestion: any) => (
              <button
                key={`${postId}-${suggestion.uid}`}
                type="button"
                onClick={() => insertMention(suggestion.username)}
                className="flex w-full items-center gap-3 rounded-2xl px-3 py-2 text-left transition hover:bg-white/10"
              >
                <div className="h-9 w-9 overflow-hidden rounded-full border border-white/15 bg-black/10">
                  {suggestion.avatar ? (
                    <img
                      src={suggestion.avatar}
                      alt={suggestion.displayName}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-xs font-black text-white/70">
                      {(suggestion.displayName || suggestion.username || "?")
                        .slice(0, 1)
                        .toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-extrabold" style={{ color: textColor }}>
                    @{suggestion.username}
                  </div>
                  <div className="truncate text-xs" style={{ color: secondaryColor }}>
                    {suggestion.displayName}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex min-w-0 items-center gap-2 w-full">
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
          placeholder={placeholder}
          className={`min-w-0 flex-1 rounded-[14px] ${inputShell} px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7657E8]/24`}
          style={{ background: panelBg, color: textColor, borderColor }}
        />
        <button
          type="button"
          onClick={() => void submit()}
          disabled={pending || !value.trim()}
          className="shrink-0 inline-flex items-center justify-center rounded-[14px] bg-[#2B6CFF] dark:bg-[#2B6CFF] px-3 sm:px-4 py-2 text-sm font-extrabold text-white shadow-[0_12px_28px_rgba(15,23,42,0.16)] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {pending ? sendingLabel : sendLabel}
        </button>
      </div>
    </div>
  );
}

export default function CommunityView(props: CommunityViewProps) {
  const { t } = useTranslation();
  const portalTarget = useRoutePortalTarget();

  const currentUserProfile = (props.currentUserProfile ??
    props.userProfile) as UserProfile;
  const isDarkMode = props.isDarkMode ?? resolveRoddoDarkMode();
  const theme = getRoddoTheme(!!isDarkMode);
  const ui = getRoddoUiTokens(!!isDarkMode);
  const scrollGlowRef = useRoddoScrollGlow<HTMLDivElement>();
  const scrollGlowStyle = useMemo(
    () => getRoddoScrollGlowStyle(!!isDarkMode),
    [isDarkMode],
  );
  const surfaceBg = ui.surfaceBg ?? theme.surface;
  const panelBg = theme.panel;
  const inputBg =
    ui.inputBg ?? (isDarkMode ? theme.panelStrong : theme.surfaceElevated);
  const previewBg =
    ui.surfaceBgSoft ??
    (isDarkMode ? theme.surfaceSoft : theme.surfaceElevated);
  const stripBg =
    ui.overlaySoft ??
    (isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(61,43,24,0.045)");
  const pillBg = isDarkMode ? DARK_PILL_BG : LIGHT_PILL_BG;
  const pillText = isDarkMode ? DARK_PILL_TXT : LIGHT_PILL_TXT;
  const viewVars = {
    "--community-page-bg": ui.pageBg ?? ui.surfaceBg ?? theme.surface,
    "--community-card-bg": ui.cardBgStrong ?? ui.cardBg ?? theme.panel,
    "--community-surface-bg":
      ui.cardBgMuted ?? ui.surfaceBgSoft ?? ui.cardBg ?? theme.surfaceElevated,
    "--community-text-primary": ui.textPrimary ?? theme.text,
    "--community-text-secondary": ui.textSecondary ?? theme.textSoft,
    "--community-border": ui.border ?? theme.border,
    "--roddo-panel-light":
      ui.cardBg ??
      "#FFF9F3",
    "--roddo-panel-light-strong":
      ui.cardBgStrong ??
      ui.cardBg ??
      "#FFFBF6",
    "--roddo-panel-dark":
      ui.cardBgMuted ??
      ui.cardBg ??
      "#151427",
    "--roddo-panel-dark-strong":
      ui.cardBgStrong ??
      ui.cardBg ??
      "#1B1830",
    "--roddo-border-light": ui.border ?? "rgba(116,86,54,0.22)",
    "--roddo-border-dark":
      ui.borderStrong ?? ui.border ?? "rgba(159,140,220,0.16)",
    "--roddo-text-light": ui.textPrimary ?? "#2b2b2b",
    "--roddo-text-light-soft": ui.textSecondary ?? "rgba(43,43,43,0.74)",
    "--roddo-text-dark": ui.textPrimary ?? "#F4F6FA",
    "--roddo-text-dark-soft": ui.textSecondary ?? "#AEB8C9",
    "--roddo-shadow-light": "0 16px 36px rgba(89,65,37,0.11)",
    "--roddo-shadow-strong-light": "0 20px 44px rgba(89,65,37,0.14)",
    "--roddo-shadow-dark": "0 18px 42px rgba(0,0,0,0.36)",
    "--roddo-shadow-strong-dark": "0 24px 56px rgba(0,0,0,0.48)",
    "--roddo-ring-light": "rgba(255,255,255,0.58)",
    "--roddo-ring-dark": "rgba(255,255,255,0.10)",
  } as React.CSSProperties;

  const requireJsAuth = props.requireJsAuth ?? false;
  const isJsAuthed = props.isJsAuthed ?? true;

  const ensureAuthedOrToast = (): boolean => {
    if (requireJsAuth && !isJsAuthed) {
      props.addToast(
        t("auth.finishingSignIn", "Finishing sign-in… try again in a moment."),
        "info",
      );
      return false;
    }
    return true;
  };

  const uid = currentUserProfile?.uid;

  if (!uid) {
    return (
      <div
        className={
          isDarkMode
            ? "roddo-personality-shell dark w-full px-3 sm:px-4 lg:px-6"
            : "roddo-personality-shell w-full px-3 sm:px-4 lg:px-6"
        }
        style={{
          ...viewVars,
          background: "var(--community-page-bg)",
          color: ui.textPrimary ?? theme.text,
        }}
      >
        <div className="space-y-4 pb-24">
          <div
            className="rounded-3xl px-4 py-4 shadow-[0_12px_30px_rgba(15,23,42,0.10)] dark:shadow-[0_22px_60px_rgba(0,0,0,0.62)]"
            style={{
              background: theme.surfaceElevated,
              border: `1px solid ${theme.border}`,
            }}
          >
            <div
              className="text-sm"
              style={{ color: ui.textSecondary ?? theme.textSoft }}
            >
              Loading profile…
            </div>
          </div>
        </div>
      </div>
    );
  }

  const posts = (props.posts ?? []) as CommunityPost[];
  const users = (props.users ?? {}) as Record<string, AppUser>;
  const commentsByPost = (props.commentsByPost ?? {}) as Record<
    string,
    Comment[]
  >;

  const onToggleLike = props.onToggleLike;
  const onToggleCommentLike = props.onToggleCommentLike;

  const onPostCreated = props.onPostCreated ?? props.onCreatePost;
  const onPostEdited = props.onPostEdited ?? props.onEditPost;
  const onPostDeleted = props.onPostDeleted ?? props.onDeletePost;

  const onCommentAdded = props.onCommentAdded ?? props.onAddComment;
  const onCommentDeleted = props.onCommentDeleted ?? props.onDeleteComment;
  const onCommentsOpened = props.onCommentsOpened;

  const safePostCreated = onPostCreated ?? (async () => {});
  const safePostEdited = onPostEdited ?? (async () => {});
  const safePostDeleted = onPostDeleted ?? (async () => {});
  const safeCommentAdded = onCommentAdded ?? (async () => {});
  const safeCommentDeleted = onCommentDeleted ?? (async () => {});
  const safeCommentEdited =
    props.onCommentEdited ?? props.onEditComment ?? (async () => {});

  const onLoadMorePosts = props.onLoadMorePosts;
  const hasMorePosts = props.hasMorePosts ?? false;
  const isLoadingMorePosts = props.isLoadingMorePosts ?? false;
  const isBootstrapped = props.isBootstrapped ?? true;
  const onRefreshFeed = props.onRefreshFeed;

  const [isRefreshingFeed, setIsRefreshingFeed] = useState(false);

  const handleRefreshFeed = async () => {
    if (!onRefreshFeed || isRefreshingFeed) return;
    try {
      setIsRefreshingFeed(true);
      await Promise.resolve(onRefreshFeed());
    } catch (e) {
      console.error("refresh feed failed", e);
      props.addToast(
        t("community.refreshError", "Could not refresh feed."),
        "error",
      );
    } finally {
      setIsRefreshingFeed(false);
    }
  };

  const COMMENTS_PREVIEW_LIMIT = 2;
  const REPLIES_PREVIEW_LIMIT = 1;

  const [active, setActive] = useState<CommunityTabId>(() =>
    normalizeInitialCommunityTab(props.viewState?.tab),
  );
  const [selectedBayId, setSelectedBayId] = useState<CommunityBayId>(() => {
    try {
      return normalizeCommunityBayId(window.localStorage.getItem(COMMUNITY_BAY_STORAGE_KEY));
    } catch {
      return "global";
    }
  });
  const [selectedDockId, setSelectedDockId] = useState<CommunityDockId>(() => {
    try {
      return normalizeCommunityDockId(window.localStorage.getItem(COMMUNITY_DOCK_STORAGE_KEY));
    } catch {
      return "main";
    }
  });
  const selectedBayMeta = useMemo(() => getCommunityBayMeta(selectedBayId), [selectedBayId]);
  const selectedDockMeta = useMemo(() => getCommunityDockMeta(selectedDockId), [selectedDockId]);

  useEffect(() => {
    try {
      window.localStorage.setItem(COMMUNITY_BAY_STORAGE_KEY, selectedBayId);
    } catch {}
  }, [selectedBayId]);

  useEffect(() => {
    try {
      window.localStorage.setItem(COMMUNITY_DOCK_STORAGE_KEY, selectedDockId);
    } catch {}
  }, [selectedDockId]);

  const [followingIds, setFollowingIds] = useState<string[]>(
    () => props.followingIds ?? [],
  );
  const followingSet = useMemo(() => new Set(followingIds), [followingIds]);

  useEffect(() => {
    setActive(normalizeInitialCommunityTab(props.viewState?.tab));
  }, [props.viewState?.tab, props.viewState?.openCrew]);

  useEffect(() => {
    if (Array.isArray(props.followingIds)) {
      setFollowingIds(props.followingIds);
      return;
    }
    if (!uid) return;
    const unsub = subscribeToFollowing(uid, setFollowingIds);
    return () => unsub?.();
  }, [uid, props.followingIds]);

  const [reportingTarget, setReportingTarget] = useState<{
    userId: string;
    userName: string;
    postId?: string | null;
  } | null>(null);
  const [reportReason, setReportReason] = useState<UserReportReason>("spam");
  const [reportDetails, setReportDetails] = useState("");
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  const [gallery, setGallery] = useState<{
    open: boolean;
    images: string[];
    startIndex: number;
    title?: string;
  }>(() => ({
    open: false,
    images: [],
    startIndex: 0,
    title: undefined,
  }));

  const openGallery = (
    images: string[],
    startIndex: number,
    title?: string,
  ) => {
    const safe = (images || []).filter(Boolean);
    if (!safe.length) return;
    const idx = Math.max(0, Math.min(safe.length - 1, startIndex || 0));
    setGallery({ open: true, images: safe, startIndex: idx, title });
  };

  const closeGallery = () =>
    setGallery({ open: false, images: [], startIndex: 0, title: undefined });

  const [newPostText, setNewPostText] = useState("");
  const newPostTextRef = useRef("");
  const [composerTextPresent, setComposerTextPresent] = useState(false);
  const [composerTextVersion, setComposerTextVersion] = useState(0);
  const [activeComposerPrompt, setActiveComposerPrompt] =
    useState<CommunityPostPromptKey>("bite");
  const [tagRod, setTagRod] = useState("");
  const [tagReel, setTagReel] = useState("");
  const [tagLureOrBait, setTagLureOrBait] = useState("");
  const [tagLBType, setTagLBType] = useState<"Lure" | "Bait">("Lure");
  const [activeTagEditor, setActiveTagEditor] = useState<
    null | "rod" | "reel" | "lb"
  >(null);

  const [feedSearch, setFeedSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setFeedSearch("");
    setSearchOpen(false);
  }, [active]);

  useEffect(() => {
    if (!searchOpen) return;
    const tid = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(tid);
  }, [searchOpen]);

  type ComposerImageItem = {
    id: string;
    previewUrl: string;
    file?: File;
    existingUrl?: string;
    objectUrl?: boolean;
  };

  const [composerImages, setComposerImages] = useState<ComposerImageItem[]>([]);
  const composerImagesRef = useRef<ComposerImageItem[]>([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [isSubmittingPost, setIsSubmittingPost] = useState(false);
  const postSubmitLockRef = useRef(false);
  const commentSubmitLockRef = useRef<Set<string>>(new Set());
  const [pendingCommentSubmitIds, setPendingCommentSubmitIds] = useState<Set<string>>(new Set());
  const MAX_IMAGES = 4;

  const [postToDelete, setPostToDelete] = useState<CommunityPost | null>(null);
  const [editingPost, setEditingPost] = useState<CommunityPost | null>(null);
  const [commentToDelete, setCommentToDelete] = useState<{
    postId: string;
    commentId: string;
  } | null>(null);

  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentDraft, setEditingCommentDraft] = useState<string>("");
  const [isSavingCommentEdit, setIsSavingCommentEdit] = useState(false);

  const [composerOpen, setComposerOpen] = useState<boolean>(() =>
    Boolean(props.viewState?.openComposer),
  );
  const [composerDetailsOpen, setComposerDetailsOpen] = useState(false);

  useEffect(() => {
    if (editingPost) setComposerOpen(false);
  }, [editingPost]);

  useEffect(() => {
    if (props.viewState?.openComposer) setComposerOpen(true);
  }, [props.viewState?.openComposer]);

  const handleComposerTextRefChange = React.useCallback((value: string) => {
    newPostTextRef.current = value;
  }, []);

  const handleComposerPresenceChange = React.useCallback((hasText: boolean) => {
    setComposerTextPresent((prev) => (prev === hasText ? prev : hasText));
  }, []);

  const composerHasAnything =
    composerTextPresent ||
    composerImages.length > 0 ||
    Boolean(tagRod.trim()) ||
    Boolean(tagReel.trim()) ||
    Boolean(tagLureOrBait.trim());

  const composerTagCount =
    (tagRod.trim() ? 1 : 0) +
    (tagReel.trim() ? 1 : 0) +
    (tagLureOrBait.trim() ? 1 : 0);

  const selectedComposerPrompt =
    COMMUNITY_POST_PROMPTS.find(
      (prompt) => prompt.key === activeComposerPrompt,
    ) || DEFAULT_COMMUNITY_POST_PROMPT;

  const composerPlaceholder = editingPost
    ? t("community.editPlaceholder", "Update your post…")
    : t(
        `community.prompt.${selectedComposerPrompt.key}.placeholder`,
        selectedComposerPrompt.placeholder,
      );

  const selectComposerPrompt = (key: CommunityPostPromptKey) => {
    setActiveComposerPrompt(key);
    setComposerOpen(true);
  };

  const collapseComposer = () => {
    if (editingPost) return;
    setNewPostText(newPostTextRef.current);
    setComposerTextVersion((v) => v + 1);
    setComposerOpen(false);
  };

  const expandComposer = () => setComposerOpen(true);

  const [commentDrafts, setCommentDrafts] = useState<Record<string, string>>(
    {},
  );
  const [replyTargets, setReplyTargets] = useState<Record<string, ReplyTarget>>(
    {},
  );
  const [expandedPosts, setExpandedPosts] = useState<Record<string, boolean>>(
    {},
  );
  const mentionableUsers = useMemo(
    () =>
      Object.values(users || {})
        .map((u: any) => {
          const username = normalizeMentionToken(
            u?.username ||
              u?.handle ||
              u?.usernameLower ||
              u?.handleLower ||
              "",
          );
          if (!username) return null;
          return {
            uid: String(u?.uid || ""),
            username,
            displayName: String(u?.displayName || u?.name || username),
            avatar: pickAvatarUrl(
              u?.avatar,
              u?.avatarUrl,
              u?.photoURL,
              u?.photoUrl,
            ),
          };
        })
        .filter((u: any) => !!u?.uid && !!u?.username && u.uid !== uid),
    [users, uid],
  );


  const [hiddenPostIds, setHiddenPostIds] = useState<Set<string>>(new Set());

  const [pendingPostLikeIds, setPendingPostLikeIds] = useState<Set<string>>(
    new Set(),
  );
  const [pendingCommentLikeKeys, setPendingCommentLikeKeys] = useState<
    Set<string>
  >(new Set());

  const [myPostLikes, setMyPostLikes] = useState<
    Record<string, boolean | CommunityReactionKey>
  >({});
  const [myCommentLikes, setMyCommentLikes] = useState<
    Record<string, boolean | CommunityReactionKey>
  >({});

  const [postReactions, setPostReactions] = useState<
    Record<string, CommunityReactionKey>
  >({});
  const [openReactionPostId, setOpenReactionPostId] = useState<string | null>(
    null,
  );
  const [openReactionCommentKey, setOpenReactionCommentKey] = useState<
    string | null
  >(null);
  const reactionHoldTimerRef = useRef<number | null>(null);
  const commentReactionHoldTimerRef = useRef<number | null>(null);
  const reactionLongPressFiredRef = useRef(false);
  const commentReactionLongPressFiredRef = useRef(false);

  const clearReactionHoldTimer = () => {
    if (reactionHoldTimerRef.current) {
      window.clearTimeout(reactionHoldTimerRef.current);
      reactionHoldTimerRef.current = null;
    }
  };

  const clearCommentReactionHoldTimer = () => {
    if (commentReactionHoldTimerRef.current) {
      window.clearTimeout(commentReactionHoldTimerRef.current);
      commentReactionHoldTimerRef.current = null;
    }
  };

  const startReactionHold = (postId: string) => {
    clearReactionHoldTimer();
    reactionLongPressFiredRef.current = false;
    commentReactionLongPressFiredRef.current = false;
    setOpenReactionCommentKey(null);
    reactionHoldTimerRef.current = window.setTimeout(() => {
      reactionLongPressFiredRef.current = true;
      setOpenReactionPostId(postId);
    }, 360);
  };

  const startCommentReactionHold = (commentKey: string) => {
    clearCommentReactionHoldTimer();
    commentReactionLongPressFiredRef.current = false;
    reactionLongPressFiredRef.current = false;
    setOpenReactionPostId(null);
    commentReactionHoldTimerRef.current = window.setTimeout(() => {
      commentReactionLongPressFiredRef.current = true;
      setOpenReactionCommentKey(commentKey);
    }, 360);
  };

  useEffect(() => {
    return () => {
      clearReactionHoldTimer();
      clearCommentReactionHoldTimer();
    };
  }, []);

  const [highlightPostId, setHighlightPostId] = useState<string | null>(null);
  const [highlightCommentKey, setHighlightCommentKey] = useState<string | null>(
    null,
  );

  useEffect(() => {
    composerImagesRef.current = composerImages;
  }, [composerImages]);

  useEffect(() => {
    return () => {
      composerImagesRef.current.forEach((item) => {
        if (!item.objectUrl) return;
        try {
          URL.revokeObjectURL(item.previewUrl);
        } catch {}
      });
    };
  }, []);

  const feedUserId = (props.viewState as any)?.feedUserId as string | undefined;

  const filteredPosts = useMemo(() => {
    const base = posts.filter((p) => !hiddenPostIds.has(p.id));
    if (!feedUserId) return base;
    return base.filter(
      (p) =>
        (p as any).authorId === feedUserId || (p as any).uid === feedUserId,
    );
  }, [posts, hiddenPostIds, feedUserId]);

  const sortedPosts = useMemo(
    () =>
      [...filteredPosts].sort(
        (a, b) => ((b as any).timestamp || 0) - ((a as any).timestamp || 0),
      ),
    [filteredPosts],
  );

  const deepLinkPostId =
    String(
      props.focusPostId ||
        (props.viewState as any)?.focusPostId ||
        props.viewState?.targetId ||
        "",
    ).trim() || null;

  const deepLinkCommentId =
    String(
      props.focusCommentId ||
        (props.viewState as any)?.focusCommentId ||
        (props.viewState as any)?.subTargetId ||
        "",
    ).trim() || null;

  useEffect(() => {
    if (!deepLinkPostId) return;
    if (active !== "feed") setActive("feed");
  }, [deepLinkPostId, active]);

  const tabFilteredPosts = useMemo(() => {
    const base =
      active === "feed"
        ? sortedPosts
        : active === "following"
          ? sortedPosts.filter((p) => {
              const a = String((p as any).authorId || (p as any).uid || "");
              return a && followingSet.has(a);
            })
          : sortedPosts;

    const bayDockFiltered = base.filter((post) => {
      const p: any = post as any;
      const matchesBay = getPostBayId(p) === selectedBayId;
      const matchesDock =
        selectedDockId === "main" || getPostDockId(p) === selectedDockId;

      return matchesBay && matchesDock;
    });

    if (!deepLinkPostId) return bayDockFiltered;
    if (bayDockFiltered.some((p) => String((p as any).id) === deepLinkPostId)) return bayDockFiltered;

    const targetPost = sortedPosts.find(
      (p) => String((p as any).id) === deepLinkPostId,
    );
    if (!targetPost) return bayDockFiltered;

    return [
      targetPost,
      ...bayDockFiltered.filter((p) => String((p as any).id) !== deepLinkPostId),
    ];
  }, [active, sortedPosts, followingSet, deepLinkPostId, selectedBayId, selectedDockId]);

  const visiblePosts = useMemo(() => {
    const needle = feedSearch.trim().toLowerCase();
    if (!needle) return tabFilteredPosts;

    const filtered = tabFilteredPosts.filter((post) => {
      const p: any = post as any;
      const authorId = String(p.authorId || p.uid || "");
      const author = users[authorId];
      const authorName = String(
        author?.displayName ??
          author?.name ??
          (author as any)?.username ??
          p.authorName ??
          p.author ??
          "",
      ).toLowerCase();
      const body = String(p.text ?? p.caption ?? "").toLowerCase();

      return authorName.includes(needle) || body.includes(needle);
    });

    if (!deepLinkPostId) return filtered;
    if (filtered.some((p) => String((p as any).id) === deepLinkPostId))
      return filtered;

    const targetPost = tabFilteredPosts.find(
      (p) => String((p as any).id) === deepLinkPostId,
    );
    return targetPost ? [targetPost, ...filtered] : filtered;
  }, [feedSearch, tabFilteredPosts, users, deepLinkPostId]);

  const communityFeedItems = useMemo(
    () =>
      insertNativeFeedAdSlots(visiblePosts, {
        enabled: Boolean(
          FEATURES.ADS_ENABLED && FEATURES.BALEEN_BAY_NATIVE_ADS_ENABLED,
        ),
        interval: 2,
        placement: "baleen-bay-feed",
      }),
    [visiblePosts],
  );

  const handleOpenUserProfile = (userId?: string) => {
    if (!userId) return;
    props.onOpenUserProfile?.(userId);
  };

  const handleOpenReport = (target: {
    userId: string;
    userName: string;
    postId?: string | null;
  }) => {
    if (!uid) {
      props.addToast(
        t("community.signInToReport", "Please sign in to report content."),
        "error",
      );
      return;
    }
    if (!target.userId) {
      props.addToast(
        t(
          "community.reportMissingTarget",
          "Could not identify what to report.",
        ),
        "error",
      );
      return;
    }
    if (target.userId === uid) {
      props.addToast(
        t("community.reportSelfError", "You cannot report yourself."),
        "error",
      );
      return;
    }

    setReportReason("spam");
    setReportDetails("");
    setReportingTarget(target);
  };

  const handleSubmitUserReport = async () => {
    if (!uid || !reportingTarget) return;

    const cleanDetails = reportDetails.trim();
    if (cleanDetails.length < 6) {
      props.addToast(
        t(
          "community.reportDetailsRequired",
          "Please add a few details for admins to review.",
        ),
        "error",
      );
      return;
    }

    try {
      setIsSubmittingReport(true);
      await submitUserReport({
        reporterUid: uid,
        reporterName: currentUserProfile?.name,
        targetUserId: reportingTarget.userId,
        targetUserName: reportingTarget.userName,
        postId: reportingTarget.postId ?? null,
        reason: reportReason,
        details: cleanDetails,
      });
      props.addToast(
        t("community.reportSubmitted", "Report submitted."),
        "success",
      );
      setReportingTarget(null);
      setReportReason("spam");
      setReportDetails("");
    } catch (err: any) {
      console.error("report failed", err);
      props.addToast(
        err?.message || t("community.reportError", "Could not submit report."),
        "error",
      );
    } finally {
      setIsSubmittingReport(false);
    }
  };

  const commentsVersion = useMemo(() => {
    if (!deepLinkPostId) return "no-deeplink";
    const arr = commentsByPost?.[deepLinkPostId];
    if (!Array.isArray(arr) || !arr.length) return `${deepLinkPostId}:0`;
    return `${deepLinkPostId}:${arr
      .map((c: any) => c?.id)
      .filter(Boolean)
      .join(",")}`;
  }, [commentsByPost, deepLinkPostId]);

  useEffect(() => {
    if (!deepLinkPostId) return;

    setExpandedPosts((prev) => {
      if (prev[deepLinkPostId]) return prev;
      return { ...prev, [deepLinkPostId]: true };
    });

    const timers: number[] = [];

    const flashPost = () => {
      setHighlightPostId(deepLinkPostId);
      timers.push(window.setTimeout(() => setHighlightPostId(null), 1800));
    };

    const flashComment = (commentId: string) => {
      setHighlightCommentKey(`${deepLinkPostId}__${commentId}`);
      timers.push(window.setTimeout(() => setHighlightCommentKey(null), 1600));
    };

    const tryScroll = () => {
      if (deepLinkCommentId) {
        const cEl = document.getElementById(
          `comment-${deepLinkPostId}-${deepLinkCommentId}`,
        );
        if (cEl) {
          cEl.scrollIntoView({ behavior: "smooth", block: "center" });
          flashPost();
          flashComment(deepLinkCommentId);
          return true;
        }
      }

      const pEl = document.getElementById(`post-${deepLinkPostId}`);
      if (pEl) {
        pEl.scrollIntoView({ behavior: "smooth", block: "start" });
        flashPost();
        return true;
      }

      return false;
    };

    timers.push(
      window.setTimeout(() => {
        if (tryScroll()) return;

        timers.push(
          window.setTimeout(() => {
            tryScroll();
          }, 250),
        );
        timers.push(
          window.setTimeout(() => {
            tryScroll();
          }, 700),
        );
      }, 120),
    );

    return () => {
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [deepLinkPostId, deepLinkCommentId, visiblePosts.length, commentsVersion]);

  const addPickedImages = async (files?: FileList | File[] | null) => {
    if (!files) return;

    const remaining = Math.max(0, MAX_IMAGES - composerImages.length);
    if (remaining <= 0) {
      props.addToast(
        t("community.imageLimit", `You can add up to ${MAX_IMAGES} photos.`),
        "info",
      );
      return;
    }

    const arr: File[] = Array.isArray(files) ? files : Array.from(files);
    const accepted: ComposerImageItem[] = [];

    for (const f of arr) {
      if (!f || accepted.length >= remaining) break;

      if (!f.type?.startsWith("image/")) {
        props.addToast(
          t("community.imageTypeError", "Please choose an image file."),
          "error",
        );
        continue;
      }

      try {
        const compressed = await compressImageFile(f, {
          maxDim: 1600,
          quality: 0.82,
          mimeType: "image/jpeg",
        });

        let finalBlob = compressed;
        if (compressed.size > 5 * 1024 * 1024) {
          finalBlob = await compressImageFile(f, {
            maxDim: 1280,
            quality: 0.72,
            mimeType: "image/jpeg",
          });
        }

        if (finalBlob.size > 5 * 1024 * 1024) {
          props.addToast(
            t(
              "community.imageSizeError",
              "Image is still too large after compression. Try a smaller photo.",
            ),
            "error",
          );
          continue;
        }

        const compressedFile = new File(
          [finalBlob],
          `${f.name.replace(/\.[^.]+$/, "") || "roddo-photo"}.jpg`,
          {
            type: "image/jpeg",
            lastModified: Date.now(),
          },
        );
        const previewUrl = URL.createObjectURL(compressedFile);
        accepted.push({
          id: `new_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          previewUrl,
          file: compressedFile,
          objectUrl: true,
        });
      } catch (err) {
        console.warn(
          "[CommunityView] Image compression failed; checking original fallback.",
          err,
        );
        if (f.size > 5 * 1024 * 1024) {
          props.addToast(
            t("community.imageSizeError", "Image must be under 5MB."),
            "error",
          );
          continue;
        }
        const previewUrl = URL.createObjectURL(f);
        accepted.push({
          id: `new_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          previewUrl,
          file: f,
          objectUrl: true,
        });
      }
    }

    if (!accepted.length) return;
    setComposerImages((prev) => [...prev, ...accepted].slice(0, MAX_IMAGES));
  };

  const removePickedImageAt = (idx: number) => {
    setComposerImages((prev) => {
      const target = prev[idx];
      if (target?.objectUrl) {
        try {
          URL.revokeObjectURL(target.previewUrl);
        } catch {}
      }
      return prev.filter((_, i) => i !== idx);
    });
  };

  const movePickedImage = (fromIndex: number, toIndex: number) => {
    setComposerImages((prev) => {
      if (
        fromIndex < 0 ||
        fromIndex >= prev.length ||
        toIndex < 0 ||
        toIndex >= prev.length ||
        fromIndex === toIndex
      ) {
        return prev;
      }
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const makePickedImagePrimary = (idx: number) => {
    if (idx <= 0) return;
    movePickedImage(idx, 0);
  };

  const clearComposer = () => {
    setNewPostText("");
    newPostTextRef.current = "";
    setComposerTextPresent(false);
    setComposerTextVersion((v) => v + 1);
    setActiveComposerPrompt("bite");
    setEditingPost(null);

    setTagRod("");
    setTagReel("");
    setTagLureOrBait("");
    setTagLBType("Lure");
    setActiveTagEditor(null);
    setComposerDetailsOpen(false);

    setComposerImages((prev) => {
      prev.forEach((item) => {
        if (!item.objectUrl) return;
        try {
          URL.revokeObjectURL(item.previewUrl);
        } catch {}
      });
      return [];
    });

    setComposerOpen(false);
  };

  const uploadCommunityImage = async (args: {
    uid: string;
    postId: string;
    file: File;
    index: number;
    storageKey?: string;
  }): Promise<string> => {
    const safeKey = String(args.storageKey || `img_${Date.now()}`)
      .replace(/[^a-zA-Z0-9_-]+/g, "_")
      .slice(0, 48);
    return await uploadImage(
      args.file,
      `community/${args.uid}/${args.postId}/${String(args.index).padStart(2, "0")}_${safeKey}.jpg`,
      {
        maxDim: 1280,
        contentType: "image/jpeg",
      },
    );
  };

  const materializeComposerImages = async (postId: string): Promise<string[]> => {
    const ordered = composerImages.slice(0, MAX_IMAGES);
    const hasNewFiles = ordered.some((item) => !!item.file);
    if (hasNewFiles) setIsUploadingImages(true);

    try {
      const resolved: string[] = [];
      for (let i = 0; i < ordered.length; i += 1) {
        const item = ordered[i];
        if (item.existingUrl && !item.file) {
          resolved.push(item.existingUrl);
          continue;
        }
        if (!item.file) continue;
        resolved.push(
          await uploadCommunityImage({
            uid: currentUserProfile.uid,
            postId,
            file: item.file,
            index: i,
            storageKey: item.id,
          }),
        );
      }
      return resolved;
    } finally {
      if (hasNewFiles) setIsUploadingImages(false);
    }
  };

  const visibleReadablePostIds = useMemo(
    () => sortedPosts.filter((p) => canReadPostClient(p, uid)).map((p) => p.id),
    [sortedPosts, uid],
  );

  const getPostLikeMeta = (post: CommunityPost): LikeMeta => {
    const p: any = post as any;
    const rawReaction = myPostLikes[post.id];
    const reaction =
      normalizeCommunityReactionKey(rawReaction) ||
      (rawReaction === true ? "like" : null);
    return {
      count:
        typeof p.likeCount === "number"
          ? p.likeCount
          : Array.isArray(p.likes)
            ? p.likes.length
            : 0,
      likedByMe: Boolean(rawReaction),
      reaction,
    };
  };

  const getCommentLikeMeta = (postId: string, comment: Comment): LikeMeta => {
    const key = `${postId}__${comment.id}`;
    const rawReaction = myCommentLikes[key];
    const reaction =
      normalizeCommunityReactionKey(rawReaction) ||
      (rawReaction === true ? "like" : null);
    return {
      count:
        typeof (comment as any).likeCount === "number"
          ? Number((comment as any).likeCount)
          : Array.isArray((comment as any).likedBy)
            ? (comment as any).likedBy.length
            : 0,
      likedByMe: Boolean(rawReaction),
      reaction,
    };
  };

  useEffect(() => {
    if (!uid || !visibleReadablePostIds.length) {
      setMyPostLikes({});
      return;
    }

    return subscribeMyPostLikes(visibleReadablePostIds, uid, setMyPostLikes);
  }, [uid, visibleReadablePostIds]);

  const visibleCommentKeys = useMemo(() => {
    const keys: { postId: string; commentId: string }[] = [];

    sortedPosts.forEach((post) => {
      if (!canReadPostClient(post, uid)) return;

      const all = (
        (commentsByPost?.[post.id] ||
          (post as any).comments ||
          []) as any[] as any[]
      ).slice();
      const topLevel = all.filter((c) => !c.parentCommentId);

      const isExpanded = Boolean((expandedPosts as any)[post.id]);
      const visibleTop = isExpanded
        ? topLevel
        : topLevel.slice(0, COMMENTS_PREVIEW_LIMIT);

      const repliesByParent: Record<string, any[]> = {};
      all.forEach((c) => {
        const pid = c.parentCommentId;
        if (!pid) return;
        if (!repliesByParent[pid]) repliesByParent[pid] = [];
        repliesByParent[pid].push(c);
      });

      visibleTop.forEach((c) => {
        if (c?.id) keys.push({ postId: post.id, commentId: c.id });

        const replies = repliesByParent[c.id] || [];
        const visibleReplies = isExpanded
          ? replies
          : replies.slice(0, REPLIES_PREVIEW_LIMIT);
        visibleReplies.forEach(
          (r) => r?.id && keys.push({ postId: post.id, commentId: r.id }),
        );
      });
    });

    return keys;
  }, [
    sortedPosts,
    expandedPosts,
    commentsByPost,
    uid,
    COMMENTS_PREVIEW_LIMIT,
    REPLIES_PREVIEW_LIMIT,
  ]);

  useEffect(() => {
    if (!uid || !visibleCommentKeys.length) {
      setMyCommentLikes({});
      return;
    }

    return subscribeMyCommentLikes(visibleCommentKeys, uid, setMyCommentLikes);
  }, [uid, visibleCommentKeys]);

  const canSubmit =
    !isUploadingImages &&
    !isSubmittingPost &&
    (composerTextPresent ||
      composerImages.length > 0 ||
      Boolean(tagRod.trim()) ||
      Boolean(tagReel.trim()) ||
      Boolean(tagLureOrBait.trim()));

  const handleCreateOrEditPost = async () => {
    if (!ensureAuthedOrToast()) return;
    if (!canSubmit) {
      props.addToast(
        t("community.emptyPostError", "Post cannot be empty."),
        "error",
      );
      return;
    }

    // Synchronous guard closes the same-frame multi-tap window before React state rerenders.
    if (postSubmitLockRef.current) return;
    postSubmitLockRef.current = true;
    setIsSubmittingPost(true);

    if (editingPost) {
      try {
        const nextCaption = newPostTextRef.current.trim();

        const nextImages = await materializeComposerImages(editingPost.id);
        const primary = nextImages[0] ?? null;

        await Promise.resolve(
          safePostEdited(editingPost.id, {
            caption: nextCaption,
            ...({ text: nextCaption } as any),
            images: nextImages,
            ...({ imageUrls: nextImages } as any),
            image: primary,
            ...({ imageUrl: primary } as any),
            rod: tagRod.trim(),
            reel: tagReel.trim(),
            lureOrBait: tagLureOrBait.trim(),
            lureOrBaitType: tagLureOrBait.trim() ? tagLBType : "",
          } as any),
        );

        props.addToast(t("community.postUpdated", "Post updated!"), "success");
        clearComposer();
      } catch (err) {
        console.error("Failed to edit post:", err);
        props.addToast(
          t("community.postEditError", "Could not update post."),
          "error",
        );
      } finally {
        postSubmitLockRef.current = false;
        setIsSubmittingPost(false);
      }
      return;
    }

    const id = `post_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    try {
      const authorName =
        currentUserProfile.name ||
        (currentUserProfile as any).displayName ||
        "Angler";
      const currentUserReputation =
        (currentUserProfile as any)?.reputation ||
        getFallbackProfileReputation(currentUserProfile as any);

      const uploadedImages = await materializeComposerImages(id);
      const primary = uploadedImages[0];

      const post: CommunityPost = {
        visibility: "public",
        bayId: selectedBayMeta.id,
        bayLabel: selectedBayMeta.label,
        dockId: selectedDockMeta.id,
        dockLabel: selectedDockMeta.label,
        roomId: selectedDockMeta.id,
        visibilityScope: selectedBayMeta.id === "global" ? "global" : "bay",
        id,
        authorId: currentUserProfile.uid,
        ...({ authorName } as any),
        ...({ author: authorName } as any),
        authorAvatar: pickAvatarUrl(
          (currentUserProfile as any).avatar,
          (currentUserProfile as any).avatarUrl,
          (currentUserProfile as any).photoURL,
          (currentUserProfile as any).photoUrl,
        ),
        authorReputation: {
          primaryBadgeKey: currentUserReputation.primaryBadgeKey,
          featuredBadgeKeys: currentUserReputation.featuredBadgeKeys,
          reputation: currentUserReputation,
        },
        caption: newPostTextRef.current.trim(),
        ...({ text: newPostTextRef.current.trim() } as any),
        ...(uploadedImages.length ? ({ images: uploadedImages } as any) : {}),
        ...(uploadedImages.length
          ? ({ imageUrls: uploadedImages } as any)
          : {}),
        ...(primary ? ({ image: primary } as any) : {}),
        ...(primary ? ({ imageUrl: primary } as any) : {}),
        rod: tagRod.trim(),
        reel: tagReel.trim(),
        lureOrBait: tagLureOrBait.trim(),
        lureOrBaitType: tagLureOrBait.trim() ? tagLBType : "",
        species: "",
        location: "",
        likes: [],
        comments: [],
        timestamp: Date.now(),
      } as any;

      await Promise.resolve(safePostCreated(post));

      clearComposer();
      props.addToast(
        t("community.postCreated", "Posted to Baleen Bay ✨"),
        "success",
      );
    } catch (err) {
      console.error("Failed to create post:", err);
      props.addToast(
        t("community.postCreateError", "Could not publish post."),
        "error",
      );
    } finally {
      postSubmitLockRef.current = false;
      setIsSubmittingPost(false);
    }
  };

  const handleConfirmDeletePost = async () => {
    if (!postToDelete) return;
    const id = postToDelete.id;

    setHiddenPostIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    try {
      await Promise.resolve(safePostDeleted(id));
      props.addToast(t("community.postDeleted", "Post deleted."), "success");
    } catch (err) {
      console.error("Failed to delete post:", err);
      props.addToast(
        t("community.postDeleteError", "Could not delete post."),
        "error",
      );

      setHiddenPostIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } finally {
      setPostToDelete(null);
    }
  };

  const handleSubmitComment = async (postId: string, draftText?: string): Promise<boolean> => {
    const text = String(draftText ?? commentDrafts[postId] ?? "").trim();
    if (!text) return false;

    onCommentsOpened?.(postId);

    if (!ensureAuthedOrToast()) return false;
    if (commentSubmitLockRef.current.has(postId)) return false;

    commentSubmitLockRef.current.add(postId);
    setPendingCommentSubmitIds((prev) => new Set(prev).add(postId));

    const parentCommentId = replyTargets[postId]?.commentId ?? null;
    const now = Date.now();
    const currentUserReputation =
      (currentUserProfile as any)?.reputation ||
      getFallbackProfileReputation(currentUserProfile as any);

    const newComment: Comment = {
      id: `comment_${now}_${Math.random().toString(36).slice(2, 8)}`,
      postId,
      text,
      authorId: currentUserProfile.uid as any,
      authorName:
        currentUserProfile.name ||
        (currentUserProfile as any).displayName ||
        "Angler",
      authorAvatar: pickAvatarUrl(
        (currentUserProfile as any).avatar,
        (currentUserProfile as any).avatarUrl,
        (currentUserProfile as any).photoURL,
        (currentUserProfile as any).photoUrl,
      ),
      authorReputation: {
        primaryBadgeKey: currentUserReputation.primaryBadgeKey,
        featuredBadgeKeys: currentUserReputation.featuredBadgeKeys,
        reputation: currentUserReputation,
      },
      authorUsername:
        String(
          (currentUserProfile as any)?.username ||
            (currentUserProfile as any)?.usernameLower ||
            (currentUserProfile as any)?.handle ||
            (currentUserProfile as any)?.handleLower ||
            "",
        ).trim() || undefined,
      createdAt: now as any,
      timestamp: now as any,
      parentCommentId,
    } as any;

    try {
      await Promise.resolve(safeCommentAdded(postId, newComment));
      setCommentDrafts((prev) => ({ ...prev, [postId]: "" }));
      setReplyTargets((prev) => ({ ...prev, [postId]: null }));
      return true;
    } catch (err) {
      console.error("Failed to add comment:", err);
      props.addToast(
        t("community.commentAddError", "Could not add comment."),
        "error",
      );
      return false;
    } finally {
      commentSubmitLockRef.current.delete(postId);
      setPendingCommentSubmitIds((prev) => {
        const next = new Set(prev);
        next.delete(postId);
        return next;
      });
    }
  };

  const handleReplyToComment = (
    postId: string,
    commentId: string,
    authorName: string,
    authorHandle?: string | null,
  ) => {
    const normalizedHandle = String(authorHandle || "")
      .trim()
      .replace(/^@+/, "")
      .toLowerCase();

    setReplyTargets((prev) => ({
      ...prev,
      [postId]: {
        commentId,
        authorName,
        authorHandle: normalizedHandle || null,
      },
    }));

    if (!normalizedHandle) return;

    setCommentDrafts((prev) => {
      const existing = prev[postId] || "";
      const mention = `@${normalizedHandle} `;
      if (existing.startsWith(mention)) return prev;
      return { ...prev, [postId]: mention + existing };
    });
  };

  const cancelReply = (postId: string) => {
    setReplyTargets((prev) => ({ ...prev, [postId]: null }));
  };

  const handleSharePost = async (post: CommunityPost) => {
    const postId = String((post as any).id ?? "").trim();
    const shareUrl = buildPublicPostUrl(postId);

    try {
      const res = await shareOut({
        url: shareUrl,
      });

      if (res.method === "clipboard") {
        props.addToast(
          t("community.shareCopied", "Share link copied — cast it out ✨"),
          "success",
        );
      }
    } catch (err) {
      console.error("Error sharing post", err);
      props.addToast(
        t("community.shareError", "Could not share this post."),
        "error",
      );
    }
  };

  const handleTogglePostLike = async (post: CommunityPost) => {
    if (!ensureAuthedOrToast()) return;
    if ((post as any).authorId === currentUserProfile.uid) {
      props.addToast(
        t("community.noSelfLike", "You can't like your own post."),
        "info",
      );
      return;
    }
    if (pendingPostLikeIds.has((post as any).id)) return;

    setPendingPostLikeIds((prev) => {
      const next = new Set(prev);
      next.add((post as any).id);
      return next;
    });

    try {
      await Promise.resolve(onToggleLike((post as any).id));
    } catch (err) {
      console.error("Failed to toggle post like", err);
      props.addToast(
        t("community.likeError", "Could not update like."),
        "error",
      );
    } finally {
      setPendingPostLikeIds((prev) => {
        const next = new Set(prev);
        next.delete((post as any).id);
        return next;
      });
    }
  };

  const handleMainPostReactionClick = async (post: CommunityPost) => {
    const postId = String((post as any).id || "");
    setOpenReactionPostId(null);

    if (reactionLongPressFiredRef.current) {
      reactionLongPressFiredRef.current = false;
      return;
    }

    const alreadyLiked = Boolean(myPostLikes[postId]);
    if (alreadyLiked) {
      setPostReactions((prev) => {
        const next = { ...prev };
        delete next[postId];
        return next;
      });
    } else {
      setPostReactions((prev) => ({ ...prev, [postId]: "like" }));
    }

    await handleTogglePostLike(post);
  };

  const handleChoosePostReaction = async (
    post: CommunityPost,
    reaction: CommunityReactionKey,
  ) => {
    const postId = String((post as any).id || "");
    if (!postId) return;

    setOpenReactionPostId(null);

    if ((post as any).authorId === currentUserProfile.uid) {
      props.addToast(
        t("community.noSelfLike", "You can't like your own post."),
        "info",
      );
      return;
    }

    setPostReactions((prev) => ({ ...prev, [postId]: reaction }));

    try {
      await Promise.resolve(onToggleLike(postId, reaction));
    } catch (err) {
      console.error("Failed to update post reaction", err);
      props.addToast(
        t("community.likeError", "Could not update reaction."),
        "error",
      );
    }
  };

  const handleToggleAnyCommentLike = async (
    postId: string,
    comment: Comment,
    reaction: CommunityReactionKey = DEFAULT_COMMUNITY_REACTION.key,
  ) => {
    if (!ensureAuthedOrToast()) return;
    const authorId = (comment as any).authorId;
    if (authorId && authorId === currentUserProfile.uid) {
      props.addToast(
        t("community.noSelfLikeComment", "You can't like your own comment."),
        "info",
      );
      return;
    }

    const key = `${postId}:${(comment as any).id}`;
    if (pendingCommentLikeKeys.has(key)) return;

    setPendingCommentLikeKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });

    try {
      await Promise.resolve(
        onToggleCommentLike(postId, (comment as any).id, reaction),
      );
    } catch (err) {
      console.error("Failed to toggle comment reaction", err);
      props.addToast(
        t("community.likeError", "Could not update reaction."),
        "error",
      );
    } finally {
      setPendingCommentLikeKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleMainCommentReactionClick = async (
    postId: string,
    comment: Comment,
  ) => {
    clearCommentReactionHoldTimer();
    if (commentReactionLongPressFiredRef.current) {
      commentReactionLongPressFiredRef.current = false;
      return;
    }

    const meta = getCommentLikeMeta(postId, comment);
    await handleToggleAnyCommentLike(
      postId,
      comment,
      meta.reaction || DEFAULT_COMMUNITY_REACTION.key,
    );
  };

  const handleChooseCommentReaction = async (
    postId: string,
    comment: Comment,
    reaction: CommunityReactionKey,
  ) => {
    setOpenReactionCommentKey(null);
    commentReactionLongPressFiredRef.current = false;
    await handleToggleAnyCommentLike(postId, comment, reaction);
  };

  const startEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentDraft(comment.text || "");
  };

  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentDraft("");
  };

  const saveEditComment = async (postId: string, commentId: string) => {
    const text = editingCommentDraft.trim();
    if (!text) return;

    try {
      setIsSavingCommentEdit(true);
      await Promise.resolve(safeCommentEdited(postId, commentId, text));
      cancelEditComment();
    } catch (err) {
      console.error("Failed to edit comment", err);
      props.addToast(
        t("community.commentEditError", "Could not update comment."),
        "error",
      );
    } finally {
      setIsSavingCommentEdit(false);
    }
  };

  const TagChip = ({
    label,
    active: isOn,
    onClick,
  }: {
    label: string;
    active: boolean;
    onClick: () => void;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={`${chipBase} !w-full !justify-center !px-2.5 !py-2 ${isOn ? chipOn : ""}`}
    >
      <span className="opacity-80">🏷️</span>
      <span className="truncate max-w-[120px] sm:max-w-none">{label}</span>
    </button>
  );

  return (
    <div
      ref={scrollGlowRef}
      className={
        isDarkMode
          ? "roddo-community-desktop-lane dark mx-auto w-full px-0"
          : "roddo-community-desktop-lane mx-auto w-full px-0"
      }
      data-roddo-tour="community-page"
      data-roddo-tactile
      data-roddo-theme={isDarkMode ? "dark" : "light"}
      style={{
        ...viewVars,
        ...scrollGlowStyle,
        backgroundColor: "var(--community-page-bg)",
        color: ui.textPrimary ?? theme.text,
      }}
    >
      <style>{roddoTactilePolishStyles}</style>
      <div className="space-y-5 pb-24 pt-3 overflow-x-hidden">
        <div className="border-b border-[var(--roddo-border-light)] px-3 pb-5 dark:border-[var(--roddo-border-dark)] sm:px-4">
          <div className="mb-3">
            <div className="text-[11px] font-black uppercase tracking-[0.16em] text-[var(--community-text-secondary)]">
              Baleen Bay
            </div>

            <div className="mt-1 flex min-w-0 items-center gap-3">
              <div className="min-w-0 flex-1 text-[15px] font-black text-[var(--community-text-primary)]">
                Find your water. Join the conversation.
              </div>

              {!searchOpen && !feedSearch.trim() ? (
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-[11px] border border-[var(--roddo-border-light)] px-3 text-[11px] font-black text-[var(--community-text-primary)] dark:border-[var(--roddo-border-dark)]"
                  style={{ background: inputBg }}
                >
                  <SearchIcon className="h-3.5 w-3.5" />
                  Search
                </button>
              ) : (
                <div className="relative min-w-0 flex-1 max-w-[230px] sm:max-w-[300px]">
                  <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--community-text-secondary)]" />
                  <input
                    ref={searchInputRef}
                    value={feedSearch}
                    onChange={(e) => setFeedSearch(e.target.value)}
                    placeholder={t("community.searchPlaceholder", "Search posts or anglers")}
                    className={`h-9 w-full rounded-[11px] ${inputShell} py-2 pl-8 pr-14 text-[12px] font-semibold focus:outline-none`}
                    style={{ background: inputBg, color: ui.textPrimary ?? theme.text, borderColor: theme.border }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (feedSearch.trim()) setFeedSearch("");
                      else setSearchOpen(false);
                    }}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-[8px] px-2 py-1 text-[10px] font-black text-[var(--community-text-secondary)] hover:bg-black/5 dark:hover:bg-white/5"
                  >
                    {feedSearch.trim() ? "Clear" : "✕"}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <label className="min-w-0">
              <span className="mb-1.5 block text-[10px] font-black uppercase tracking-[0.12em] text-[var(--community-text-secondary)]">
                Bay
              </span>
              <div className="relative">
                <select
                  value={selectedBayId}
                  onChange={(e) => setSelectedBayId(normalizeCommunityBayId(e.target.value))}
                  className={`w-full appearance-none rounded-[13px] ${inputShell} px-3 py-2.5 pr-8 text-[12px] font-black focus:outline-none`}
                  style={{ background: inputBg, color: ui.textPrimary ?? theme.text, borderColor: theme.border }}
                >
                  {COMMUNITY_BAYS.map((bay) => (
                    <option key={bay.id} value={bay.id}>
                      {bay.emoji} {bay.shortLabel}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black text-[var(--community-text-secondary)]">▾</span>
              </div>
            </label>

            <label className="min-w-0">
              <span className="mb-1.5 block text-[10px] font-black uppercase tracking-[0.12em] text-[var(--community-text-secondary)]">
                Dock
              </span>
              <div className="relative">
                <select
                  value={selectedDockId}
                  onChange={(e) => setSelectedDockId(normalizeCommunityDockId(e.target.value))}
                  className={`w-full appearance-none rounded-[13px] ${inputShell} px-3 py-2.5 pr-8 text-[12px] font-black focus:outline-none`}
                  style={{ background: inputBg, color: ui.textPrimary ?? theme.text, borderColor: theme.border }}
                >
                  {COMMUNITY_DOCKS.map((dock) => (
                    <option key={dock.id} value={dock.id}>
                      {dock.emoji} {dock.shortLabel}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black text-[var(--community-text-secondary)]">▾</span>
              </div>
            </label>
          </div>
        </div>

        <div className="border-b border-[var(--roddo-border-light)] px-3 pb-5 dark:border-[var(--roddo-border-dark)] sm:px-4">
          <div
            className="cursor-pointer"
            role="button"
            tabIndex={0}
            onClick={() => (composerOpen ? undefined : expandComposer())}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && !composerOpen) expandComposer();
            }}
            title={composerOpen ? undefined : "Tap to post"}
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-[#d9b85c]/35">
                <img
                  src={pickAvatarUrl(
                    (currentUserProfile as any).avatar,
                    (currentUserProfile as any).avatarUrl,
                    (currentUserProfile as any).photoURL,
                    (currentUserProfile as any).photoUrl,
                  )}
                  alt={currentUserProfile?.name || "You"}
                  className="h-full w-full object-cover"
                />
              </div>

              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-black text-[var(--community-text-primary)]">
                  Share with Baleen Bay
                </div>
                <div className="mt-0.5 truncate text-[11px] font-semibold text-[var(--community-text-secondary)]">
                  {composerOpen
                    ? `${selectedBayMeta.emoji} ${selectedBayMeta.shortLabel} · ${selectedDockMeta.emoji} ${selectedDockMeta.shortLabel}`
                    : composerHasAnything
                      ? (newPostTextRef.current.trim() || "Continue your post")
                      : "Catch, water, setup, question, or quick note"}
                </div>
              </div>

              {composerOpen ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    collapseComposer();
                  }}
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-xl font-black text-[var(--community-text-secondary)] hover:bg-black/5 dark:hover:bg-white/5"
                  aria-label={t("common.close", "Close")}
                >
                  ×
                </button>
              ) : !composerOpen ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    expandComposer();
                  }}
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-full"
                  style={{ background: pillBg, color: pillText }}
                  aria-label={t("community.open", "Add post")}
                >
                  <PlusCircleIcon className="h-7 w-7" />
                </button>
              ) : null}
            </div>
          </div>

          {composerOpen && !editingPost ? (
            <div className="mt-4 space-y-3" onClick={(e) => e.stopPropagation()}>
              {!editingPost ? (
                <label className="block">
                  <span className="mb-1.5 block text-[11px] font-black text-[var(--community-text-secondary)]">
                    Post type
                  </span>
                  <div className="relative">
                    <select
                      value={activeComposerPrompt}
                      onChange={(e) => selectComposerPrompt(e.target.value as CommunityPostPromptKey)}
                      className={`w-full appearance-none rounded-[13px] ${inputShell} px-3 py-2.5 pr-8 text-[13px] font-black focus:outline-none`}
                      style={{ background: inputBg, color: ui.textPrimary ?? theme.text, borderColor: theme.border }}
                    >
                      {COMMUNITY_POST_PROMPTS.map((prompt) => (
                        <option key={prompt.key} value={prompt.key}>
                          {prompt.emoji} {prompt.label}
                        </option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black text-[var(--community-text-secondary)]">▾</span>
                  </div>
                </label>
              ) : null}

              <FastComposerTextArea
                seed={newPostText}
                seedVersion={composerTextVersion}
                placeholder={composerPlaceholder}
                inputBg={inputBg}
                color={ui.textPrimary ?? theme.text}
                borderColor={theme.border}
                onTextRefChange={handleComposerTextRefChange}
                onPresenceChange={handleComposerPresenceChange}
                onDropFiles={(files) => addPickedImages(files)}
              />

              {composerImages.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-[11px] font-black text-[var(--community-text-primary)]">
                      Arrange photos
                    </div>
                    <div className="text-[10px] font-semibold text-[var(--community-text-secondary)]">
                      Tap a photo to make it main
                    </div>
                  </div>

                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {composerImages.map((item, idx) => (
                      <div
                        key={item.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => makePickedImagePrimary(idx)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            makePickedImagePrimary(idx);
                          }
                        }}
                        className={`relative h-24 w-24 shrink-0 overflow-hidden rounded-[14px] border text-left transition ${
                          idx === 0
                            ? "border-[#ffcd64] ring-2 ring-[#ffcd64]/35"
                            : "border-black/10 hover:border-[#ffcd64]/55 dark:border-white/10"
                        }`}
                        style={{ background: previewBg }}
                        title={idx === 0 ? "Main photo" : "Tap to make main photo"}
                        aria-label={idx === 0 ? "Main photo" : `Make photo ${idx + 1} the main photo`}
                      >
                        <img
                          src={item.previewUrl}
                          alt={`Preview ${idx + 1}`}
                          className="h-full w-full object-cover"
                        />

                        <div className="absolute left-1 top-1 flex items-center gap-1">
                          <span className="rounded-full bg-black/70 px-2 py-1 text-[9px] font-black text-white">
                            {idx === 0 ? "MAIN" : `#${idx + 1}`}
                          </span>
                        </div>

                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            removePickedImageAt(idx);
                          }}
                          className="absolute right-1 top-1 rounded-full bg-black/70 px-2 py-1 text-[10px] text-white"
                          title="Remove"
                          aria-label={`Remove photo ${idx + 1}`}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {composerDetailsOpen ? (
                <div className="space-y-3 rounded-[14px] border border-[var(--roddo-border-light)] bg-[var(--roddo-panel-light-strong)] p-3 dark:border-[var(--roddo-border-dark)] dark:bg-[var(--roddo-panel-dark-strong)]">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-[12px] font-black text-[var(--community-text-primary)]">Gear details</div>
                      <div className="mt-0.5 text-[10px] font-semibold text-[var(--community-text-secondary)]">Optional tags for your setup</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setComposerDetailsOpen(false);
                        setActiveTagEditor(null);
                      }}
                      className="text-[11px] font-black text-[var(--community-text-secondary)]"
                    >
                      Done
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <TagChip
                      label={tagRod.trim() ? `Rod: ${tagRod.trim()}` : "Rod"}
                      active={activeTagEditor === "rod"}
                      onClick={() => setActiveTagEditor((v) => (v === "rod" ? null : "rod"))}
                    />
                    <TagChip
                      label={tagReel.trim() ? `Reel: ${tagReel.trim()}` : "Reel"}
                      active={activeTagEditor === "reel"}
                      onClick={() => setActiveTagEditor((v) => (v === "reel" ? null : "reel"))}
                    />
                    <TagChip
                      label={tagLureOrBait.trim() ? `${tagLBType}: ${tagLureOrBait.trim()}` : "Lure/Bait"}
                      active={activeTagEditor === "lb"}
                      onClick={() => setActiveTagEditor((v) => (v === "lb" ? null : "lb"))}
                    />
                  </div>

                  {activeTagEditor === "rod" ? (
                    <input
                      value={tagRod}
                      onChange={(e) => setTagRod(e.target.value)}
                      placeholder="Rod name"
                      className={`w-full rounded-[12px] ${inputShell} px-3 py-2.5 text-sm focus:outline-none`}
                      style={{ background: inputBg, color: ui.textPrimary ?? theme.text, borderColor: theme.border }}
                    />
                  ) : null}

                  {activeTagEditor === "reel" ? (
                    <input
                      value={tagReel}
                      onChange={(e) => setTagReel(e.target.value)}
                      placeholder="Reel name"
                      className={`w-full rounded-[12px] ${inputShell} px-3 py-2.5 text-sm focus:outline-none`}
                      style={{ background: inputBg, color: ui.textPrimary ?? theme.text, borderColor: theme.border }}
                    />
                  ) : null}

                  {activeTagEditor === "lb" ? (
                    <div className="grid gap-2 sm:grid-cols-[auto_auto_1fr]">
                      <button
                        type="button"
                        onClick={() => setTagLBType("Lure")}
                        className={`rounded-[11px] border px-3 py-2 text-xs font-black ${tagLBType === "Lure" ? "ring-2 ring-[#ffcd64]/35" : ""}`}
                        style={{ background: pillBg, color: pillText }}
                      >
                        Lure
                      </button>
                      <button
                        type="button"
                        onClick={() => setTagLBType("Bait")}
                        className={`rounded-[11px] border px-3 py-2 text-xs font-black ${tagLBType === "Bait" ? "ring-2 ring-[#ffcd64]/35" : ""}`}
                        style={{ background: pillBg, color: pillText }}
                      >
                        Bait
                      </button>
                      <input
                        value={tagLureOrBait}
                        onChange={(e) => setTagLureOrBait(e.target.value)}
                        placeholder={`${tagLBType} name`}
                        className={`min-w-0 rounded-[12px] ${inputShell} px-3 py-2.5 text-sm focus:outline-none`}
                        style={{ background: inputBg, color: ui.textPrimary ?? theme.text, borderColor: theme.border }}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}

              {isUploadingImages ? (
                <div className={`text-xs ${txtSub}`}>{t("community.uploading", "Uploading photos…")}</div>
              ) : null}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const input = document.getElementById("community-image-input") as HTMLInputElement | null;
                    input?.click();
                  }}
                  className="inline-flex h-10 items-center gap-2 rounded-[12px] border border-[var(--roddo-border-light)] px-3 text-[12px] font-black text-[var(--community-text-primary)] dark:border-[var(--roddo-border-dark)]"
                >
                  <CameraIcon className="h-4 w-4" />
                  Photo
                </button>
                <input
                  id="community-image-input"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    void addPickedImages(e.target.files);
                    e.currentTarget.value = "";
                  }}
                />

                <button
                  type="button"
                  onClick={() => setComposerDetailsOpen((value) => !value)}
                  className="inline-flex h-10 items-center rounded-[12px] border border-[var(--roddo-border-light)] px-3 text-[12px] font-black text-[var(--community-text-primary)] dark:border-[var(--roddo-border-dark)]"
                >
                  {composerDetailsOpen ? "Hide details" : composerTagCount ? `Details · ${composerTagCount}` : "Add details"}
                </button>

                <button
                  type="button"
                  onClick={handleCreateOrEditPost}
                  disabled={!canSubmit || isSubmittingPost}
                  className={`ml-auto h-10 rounded-[12px] px-4 text-[12px] font-black transition ${
                    canSubmit
                      ? "bg-[#ffcd64] text-[#2d2b42] hover:-translate-y-0.5"
                      : "bg-[var(--community-surface-bg)] text-[var(--community-text-secondary)] opacity-60"
                  }`}
                >
                  {isSubmittingPost
                    ? (editingPost ? t("community.saving", "Saving…") : t("community.posting", "Posting…"))
                    : editingPost
                      ? t("community.save", "Save")
                      : t("community.post", "Post")}
                </button>
              </div>

              {composerHasAnything && !editingPost ? (
                <button
                  type="button"
                  onClick={() => clearComposer()}
                  className="text-[11px] font-bold text-[var(--community-text-secondary)] hover:underline"
                >
                  {t("common.clear", "Clear draft")}
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="my-4 h-px bg-[var(--roddo-border-light)] dark:bg-[var(--roddo-border-dark)]" />

          <div className="flex min-w-0 items-center gap-1.5 overflow-x-auto pb-0.5 pr-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {(
              [
                { id: "feed" as const, label: "Feed" },
                { id: "following" as const, label: "Following" },
                { id: "trending" as const, label: "Trending" },
              ] as const
            ).map((tab) => {
              const isOn = active === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActive(tab.id)}
                  aria-current={isOn ? "page" : undefined}
                  className="shrink-0 rounded-[11px] border px-3 py-2 text-[12px] font-black transition"
                  style={
                    isOn
                      ? { background: isDarkMode ? "#7657E8" : "#2B6CFF", borderColor: "rgba(255,255,255,0.16)", color: "#fff" }
                      : { background: "transparent", borderColor: isDarkMode ? "var(--roddo-border-dark)" : "var(--roddo-border-light)", color: ui.textSecondary }
                  }
                >
                  {tab.label}
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => props.navigateTo?.(View.Crew, { source: "community", tab: "board" })}
              className="shrink-0 rounded-[11px] border px-3 py-2 text-[12px] font-black"
              style={{
                background: isDarkMode ? "rgba(112,87,232,0.10)" : "rgba(43,108,255,0.06)",
                borderColor: isDarkMode ? "var(--roddo-border-dark)" : "var(--roddo-border-light)",
                color: ui.textPrimary,
              }}
            >
              Crew
            </button>
          </div>
        </div>

        {!isBootstrapped && (
          <div className="px-3 py-4 sm:px-4">
            <div className="flex items-center justify-between gap-3">
              <div className={`text-sm ${txtSub}`}>
                {t("community.loadingFeed", "Loading feed…")}
              </div>

              {onRefreshFeed ? (
                <button
                  type="button"
                  onClick={handleRefreshFeed}
                  disabled={isRefreshingFeed}
                  className={`${yellowBtn} ${isRefreshingFeed ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  {isRefreshingFeed
                    ? t("community.loading", "Loading…")
                    : t("community.retry", "Retry")}
                </button>
              ) : null}
            </div>
          </div>
        )}

        {isBootstrapped && visiblePosts.length === 0 && (
          <div className="space-y-3 px-3 py-4 sm:px-4">
            <div className="flex items-start gap-3">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[15px] border border-[#ffcd64]/70 bg-[#ffcd64]/25 text-2xl shadow-[0_12px_28px_rgba(15,23,42,0.12)]">
                🌊
              </div>
              <div className="min-w-0">
                <p className="text-base font-black text-[var(--community-text-primary)]">
                  {t(
                    "community.emptyFeedTitle",
                    "The Bay is ready for the first signal.",
                  )}
                </p>
                <p className={`mt-1 text-sm ${txtSub}`}>
                  {t(
                    "community.emptyFeedBody",
                    "Fresh water, quiet feed. Post a quick bite clue, water report, setup note, or question so the next angler has something useful to read.",
                  )}
                </p>
                <div className="mt-3 rounded-2xl border border-[var(--roddo-border-light)] bg-[var(--roddo-panel-light-strong)] px-3 py-2 text-xs font-extrabold text-[var(--roddo-text-light)] dark:border-[var(--roddo-border-dark)] dark:bg-[var(--roddo-panel-dark-strong)] dark:text-[var(--roddo-text-dark)]">
                  ✨ New here? Try “Water report” or “Ask the Bay” — no trophy
                  catch required.
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {COMMUNITY_POST_PROMPTS.slice(0, 4).map((prompt) => (
                <button
                  key={prompt.key}
                  type="button"
                  onClick={() => selectComposerPrompt(prompt.key)}
                  className="inline-flex items-center gap-1.5 rounded-full border-2 border-[var(--roddo-border-light)] bg-[var(--roddo-panel-light-strong)] px-3 py-2 text-xs font-extrabold text-[var(--roddo-text-light)] shadow-[0_10px_22px_rgba(15,23,42,0.10)] transition-transform hover:-translate-y-0.5 active:translate-y-0 dark:border-[var(--roddo-border-dark)] dark:bg-[var(--roddo-panel-dark-strong)] dark:text-[var(--roddo-text-dark)]"
                >
                  <span>{prompt.emoji}</span>
                  <span>{prompt.shortLabel}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="h-1" aria-hidden="true" />

        {communityFeedItems.map((item) => {
          if (item.kind === "ad") {
            return (
              <NativeAdCard
                key={item.id}
                slotId={item.id}
                className={flatCardInner}
              />
            );
          }

          const post = item.post;
          const p: any = post as any;

          const authorId = String(p.authorId || "");
          const author = users[authorId];

          const authorName = String(
            author?.displayName ||
              author?.name ||
              (author as any)?.username ||
              p.authorName ||
              p.author ||
              "Angler",
          );
          const isAuthor = authorId === uid;

          const authorAvatar = pickAvatarUrl(
            author?.avatar,
            (author as any)?.avatarUrl,
            (author as any)?.photoURL,
            (author as any)?.photoUrl,
            p.authorAvatar,
            (p as any).authorAvatarUrl,
            (p as any).authorPhotoURL,
            (p as any).photoURL,
          );

          const rawBodyText = String(p.text ?? p.caption ?? "");
          const imgs = getPostImages(post);

          const postLikePending = pendingPostLikeIds.has(p.id);

          const meta = getPostLikeMeta(p);
          const legacyLikesArr = p.likes || [];
          const legacyLikedByArr = p.likedBy || [];

          const isLiked =
            meta?.likedByMe ??
            (Array.isArray(legacyLikesArr)
              ? legacyLikesArr.includes(uid)
              : Array.isArray(legacyLikedByArr)
                ? legacyLikedByArr.includes(uid)
                : false);

          const likeCount =
            meta?.count ??
            (Array.isArray(legacyLikesArr)
              ? legacyLikesArr.length
              : Array.isArray(legacyLikedByArr)
                ? legacyLikedByArr.length
                : 0);

          const postReactionKey = isLiked
            ? postReactions[String(p.id)] || meta?.reaction || "like"
            : null;
          const postReaction = postReactionKey
            ? getCommunityReactionByKey(postReactionKey)
            : DEFAULT_COMMUNITY_REACTION;
          const isReactionTrayOpen = openReactionPostId === String(p.id);

          const postComments =
            (commentsByPost?.[post.id] as any[]) ??
            ((post as any).comments as any[]) ??
            [];
          const storedCommentCount = Number(
            (p as any).commentCount ?? (post as any).commentCount ?? 0,
          );
          const displayCommentCount = Math.max(
            storedCommentCount,
            postComments.length,
          );

          const sortedComments = [...postComments].sort((a, b) => {
            const at =
              (a?.createdAt?.seconds
                ? a.createdAt.seconds * 1000
                : a?.timestamp) ?? 0;
            const bt =
              (b?.createdAt?.seconds
                ? b.createdAt.seconds * 1000
                : b?.timestamp) ?? 0;
            return at - bt;
          });

          const topLevelComments = sortedComments.filter(
            (c: any) => !c.parentCommentId,
          );

          const isExpanded = Boolean(expandedPosts[p.id]);
          const visibleComments = isExpanded
            ? topLevelComments
            : topLevelComments.slice(0, COMMENTS_PREVIEW_LIMIT);

          const repliesByParent: Record<string, any[]> = {};
          sortedComments.forEach((c: any) => {
            const parentId = c.parentCommentId;
            if (!parentId) return;
            if (!repliesByParent[parentId]) repliesByParent[parentId] = [];
            repliesByParent[parentId].push(c);
          });

          const catchMetaPill = buildCatchMetaPill(post);
          const hasGearOrCatchMeta =
            !!catchMetaPill &&
            (String(p.rod || "").trim() ||
              String(p.reel || "").trim() ||
              String(p.lureOrBait || "").trim() ||
              String(p.species || "").trim() ||
              String(p.location || "").trim());

          const bodyText = hasGearOrCatchMeta
            ? cleanBodyTextWhenMetaPillIsShown(rawBodyText)
            : String(rawBodyText || "").trim();
          const showBodyText = Boolean(bodyText);

          const isFollowingAuthor = !isAuthor && followingSet.has(authorId);
          const isHighlighted = highlightPostId === String(p.id);
          const postMood = getCommunityPostMood(post);
          const postMoodStyle = getCommunityPostMoodStyle(
            postMood.tone,
            !!isDarkMode,
          );
          const storyCardStyle = getCommunityStoryCardStyle(
            postMood.tone,
            !!isDarkMode,
          );
          const storyLine = getCommunityStoryLine(
            postMood,
            displayCommentCount,
            likeCount,
          );

          return (
            <div
              key={p.id}
              id={`post-${p.id}`}
              className={`roddo-community-story-card roddo-polish-card space-y-3 border-b border-[var(--roddo-border-light)] px-3 py-4 transition-all dark:border-[var(--roddo-border-dark)] sm:px-4 ${isHighlighted ? "ring-2 ring-[#7657E8]/40" : ""}`}
              style={storyCardStyle}
            >
              <div className="flex items-start justify-between gap-3">
                <button
                  type="button"
                  onClick={() => handleOpenUserProfile(authorId)}
                  className="flex items-start gap-3 text-left hover:opacity-90"
                  title={t("community.viewProfile", "View profile")}
                >
                  <div className="w-10 h-10 rounded-full overflow-hidden border border-[#d9b85c]/45 shadow-[0_5px_14px_rgba(89,65,37,0.08)]">
                    <img
                      src={authorAvatar}
                      alt={authorName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                      <p className="truncate text-base font-extrabold text-[var(--community-text-primary)]">
                        {authorName}
                      </p>
                      <span
                        className="inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em]"
                        style={postMoodStyle}
                      >
                        <span aria-hidden="true">{postMood.icon}</span>
                        {postMood.label}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-[var(--community-text-secondary)]">
                      {storyLine} · {timeAgo(p.timestamp)}
                    </p>
                  </div>
                </button>

                <PostMenu
                  extraItems={
                    !isAuthor ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handleOpenUserProfile(authorId)}
                          className="w-full text-left px-4 py-3 text-sm font-semibold transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                          style={{ color: ui.textPrimary ?? theme.text }}
                        >
                          👤 View profile
                        </button>

                        <button
                          type="button"
                          onClick={async () => {
                            if (!uid) return;
                            try {
                              if (isFollowingAuthor) {
                                await Promise.resolve(
                                  unfollowUser(uid, authorId),
                                );
                                props.addToast(
                                  t("community.unfollowed", "Unfollowed."),
                                  "success",
                                );
                              } else {
                                await Promise.resolve(
                                  followUser(uid, authorId),
                                );
                                props.addToast(
                                  t("community.followed", "Following!"),
                                  "success",
                                );
                              }
                            } catch (e) {
                              console.error("follow/unfollow failed", e);
                              props.addToast(
                                t(
                                  "community.followError",
                                  "Could not update follow.",
                                ),
                                "error",
                              );
                            }
                          }}
                          className="w-full text-left px-4 py-3 text-sm font-semibold transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                          style={{ color: ui.textSecondary ?? theme.textSoft }}
                        >
                          {isFollowingAuthor ? "➖ Unfollow" : "➕ Follow"}
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            handleOpenReport({
                              userId: authorId,
                              userName: authorName,
                              postId: String(p.id || "") || null,
                            })
                          }
                          className="w-full text-left px-4 py-3 text-sm font-semibold transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                          style={{ color: "#ff4d6d" }}
                        >
                          🚩 Report
                        </button>
                      </>
                    ) : null
                  }
                  onEdit={
                    isAuthor
                      ? () => {
                          setEditingPost(post);
                          setNewPostText(bodyText || "");
                          newPostTextRef.current = bodyText || "";
                          setComposerTextPresent(Boolean((bodyText || "").trim()));
                          setComposerTextVersion((v) => v + 1);

                          const existing = getPostImages(post);
                          setComposerImages(
                            existing.slice(0, MAX_IMAGES).map((url, idx) => ({
                              id: `existing_${idx}_${url}`,
                              previewUrl: url,
                              existingUrl: url,
                              objectUrl: false,
                            })),
                          );

                          setTagRod(String(p.rod || ""));
                          setTagReel(String(p.reel || ""));
                          setTagLureOrBait(String(p.lureOrBait || ""));
                          setTagLBType(
                            String(p.lureOrBaitType || "").toLowerCase() ===
                              "bait"
                              ? "Bait"
                              : "Lure",
                          );

                          setActiveTagEditor(null);
                        }
                      : undefined
                  }
                  onDelete={isAuthor ? () => setPostToDelete(post) : undefined}
                />
              </div>

              {hasGearOrCatchMeta && catchMetaPill && (
                <div className="flex flex-wrap gap-2">
                  <div
                    className={`${softPill} `}
                    style={postMoodStyle}
                  >
                    <span className="mr-1" aria-hidden="true">
                      🎣
                    </span>
                    {catchMetaPill}
                  </div>
                </div>
              )}

              {showBodyText ? (
                <p className="text-[15.5px] leading-relaxed whitespace-pre-line text-[var(--community-text-primary)]">
                  {bodyText}
                </p>
              ) : null}

              {imgs.length > 0 ? (
                <BazaarStyleInlineGallery
                  images={imgs}
                  title={authorName}
                  onOpen={(startIndex) =>
                    openGallery(imgs, startIndex, authorName)
                  }
                />
              ) : null}

              <div className="mt-1 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="relative">
                    {isReactionTrayOpen && (
                      <div
                        className="roddo-reaction-tray absolute bottom-[calc(100%+10px)] left-0 z-30 flex items-center gap-1.5 rounded-full border border-white/15 bg-[#181235]/95 px-2 py-1.5 shadow-[0_16px_34px_rgba(0,0,0,0.42)]  dark:bg-[#181235]/95"
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {COMMUNITY_REACTIONS.map((reaction) => (
                          <button
                            key={reaction.key}
                            type="button"
                            onClick={() =>
                              handleChoosePostReaction(post, reaction.key)
                            }
                            className={`roddo-reaction-option grid h-10 w-10 place-items-center rounded-full ${reaction.tone} text-xl shadow-[0_6px_14px_rgba(0,0,0,0.18)]`}
                            title={reaction.label}
                            aria-label={reaction.label}
                          >
                            <span aria-hidden="true">{reaction.emoji}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => handleMainPostReactionClick(post)}
                      onMouseDown={() => startReactionHold(String(p.id))}
                      onMouseUp={clearReactionHoldTimer}
                      onMouseLeave={clearReactionHoldTimer}
                      onTouchStart={() => startReactionHold(String(p.id))}
                      onTouchEnd={clearReactionHoldTimer}
                      onContextMenu={(e) => e.preventDefault()}
                      disabled={postLikePending}
                      className={`${isLiked ? yellowBtn : softBtn} min-w-[48px] justify-center px-3 ${postLikePending ? "opacity-60 cursor-not-allowed" : ""}`}
                      title={
                        isAuthor
                          ? "You can't react to your own post"
                          : "Tap to react. Press and hold for more reactions."
                      }
                      aria-label={
                        isLiked
                          ? `${postReaction.label} reaction${likeCount > 0 ? `, ${likeCount}` : ""}`
                          : "React"
                      }
                    >
                      <span
                        className="text-base leading-none"
                        aria-hidden="true"
                      >
                        {isLiked ? postReaction.emoji : "♡"}
                      </span>
                      {likeCount > 0 ? (
                        <span className="text-xs font-black leading-none">
                          {likeCount}
                        </span>
                      ) : null}
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      onCommentsOpened?.(p.id);
                      setExpandedPosts((prev) => ({ ...prev, [p.id]: true }));
                      const el = document.getElementById(`comments-${p.id}`);
                      el?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className={`${softBtn} min-w-[48px] justify-center px-3`}
                    title={t("community.comments", "{{count}} Comments", {
                      count: displayCommentCount,
                    })}
                    aria-label={t("community.comments", "{{count}} Comments", {
                      count: displayCommentCount,
                    })}
                  >
                    <MessageSquareIcon className="w-4 h-4" />
                    {displayCommentCount > 0 ? (
                      <span className="text-xs font-black leading-none">
                        {displayCommentCount}
                      </span>
                    ) : null}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => handleSharePost(post)}
                  className={`${softBtnGhost} px-3`}
                  title={t("community.share", "Share")}
                  aria-label={t("community.share", "Share")}
                >
                  <ShareIcon className="w-4 h-4 opacity-80 hover:opacity-100 transition-opacity" />
                </button>
              </div>

              <div id={`comments-${p.id}`} className="space-y-1.5 pt-1.5">
                {visibleComments.length > 0 && (
                  <ul className="space-y-1.25">
                    {visibleComments.map((comment: any) => {
                      const isOP = comment.authorId === authorId;
                      const isOwnComment = comment.authorId === uid;

                      const cMeta = getCommentLikeMeta(p.id, comment);

                      const legacyLikedBy = Array.isArray(comment.likedBy)
                        ? comment.likedBy
                        : [];
                      const commentLikeCount =
                        cMeta?.count ?? legacyLikedBy.length;
                      const commentLikedByMe = Boolean(
                        cMeta?.likedByMe ?? legacyLikedBy.includes(uid),
                      );
                      const commentReaction = getCommunityReactionByKey(
                        cMeta?.reaction || (commentLikedByMe ? "like" : null),
                      );
                      const commentReactionKey = `${p.id}__${comment.id}`;

                      const replies = (repliesByParent[comment.id] || []).sort(
                        (a, b) => {
                          const at =
                            (a?.createdAt?.seconds
                              ? a.createdAt.seconds * 1000
                              : a?.timestamp) ?? 0;
                          const bt =
                            (b?.createdAt?.seconds
                              ? b.createdAt.seconds * 1000
                              : b?.timestamp) ?? 0;
                          return at - bt;
                        },
                      );

                      const visibleReplies = isExpanded
                        ? replies
                        : replies.slice(0, REPLIES_PREVIEW_LIMIT);

                      const pendingKey = `${p.id}:${comment.id}`;
                      const pending = pendingCommentLikeKeys.has(pendingKey);

                      const isCommentHighlighted =
                        highlightCommentKey === `${p.id}__${comment.id}`;
                      const commentAuthorId = String(comment.authorId || "");
                      const commentAuthor = users?.[commentAuthorId] as any;
                      const commentAuthorName = String(
                        commentAuthor?.displayName ||
                          commentAuthor?.name ||
                          commentAuthor?.username ||
                          comment.authorName ||
                          "Angler",
                      );
                      const commentAuthorAvatar = pickAvatarUrl(
                        commentAuthor?.avatar,
                        commentAuthor?.avatarUrl,
                        commentAuthor?.photoURL,
                        commentAuthor?.photoUrl,
                        comment.authorAvatar,
                        comment.authorAvatarUrl,
                        comment.authorPhotoURL,
                        comment.photoURL,
                      );

                      return (
                        <li key={comment.id} className="space-y-1.5">
                          <div
                            id={`comment-${p.id}-${comment.id}`}
                            className={`${RESP_CARD} transition-all ${isCommentHighlighted ? "ring-4 ring-[#ffcd64]/70" : ""}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex min-w-0 flex-1 items-start gap-2.5">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleOpenUserProfile(commentAuthorId)
                                  }
                                  className="mt-0.5 h-8 w-8 shrink-0 overflow-hidden rounded-full border border-[#d9b85c]/40 bg-white/10 shadow-[0_3px_10px_rgba(89,65,37,0.06)] transition hover:-translate-y-0.5"
                                  title={t(
                                    "community.viewProfile",
                                    "View profile",
                                  )}
                                  aria-label={t(
                                    "community.viewProfile",
                                    "View profile",
                                  )}
                                >
                                  <img
                                    src={commentAuthorAvatar}
                                    alt={commentAuthorName}
                                    className="h-full w-full object-cover"
                                  />
                                </button>

                                <div className="min-w-0 flex-1">
                                  <div className={RESP_USER}>
                                    <div className="flex min-w-0 items-center">
                                      <button
                                        type="button"
                                        className="truncate hover:underline"
                                        onClick={() =>
                                          handleOpenUserProfile(commentAuthorId)
                                        }
                                      >
                                        {commentAuthorName}
                                      </button>
                                      <BadgeIconRow
                                        reputation={resolveAuthorReputation(
                                          comment,
                                          commentAuthor,
                                        )}
                                        max={2}
                                      />
                                    </div>
                                    {isOP ? (
                                      <span className="opacity-60">• OP</span>
                                    ) : null}
                                  </div>

                                  {editingCommentId === comment.id ? (
                                    <div className="mt-2 space-y-2">
                                      <textarea
                                        rows={3}
                                        value={editingCommentDraft}
                                        onChange={(e) =>
                                          setEditingCommentDraft(e.target.value)
                                        }
                                        className={`w-full rounded-2xl ${inputShell} px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffcd64]/70`}
                                        style={{
                                          background: panelBg,
                                          color: ui.textPrimary ?? theme.text,
                                          borderColor: theme.border,
                                        }}
                                      />
                                      <div className="flex items-center gap-2">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            saveEditComment(p.id, comment.id)
                                          }
                                          disabled={
                                            !editingCommentDraft.trim() ||
                                            isSavingCommentEdit
                                          }
                                          className={yellowBtn}
                                        >
                                          {isSavingCommentEdit
                                            ? "Saving…"
                                            : "Save"}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={cancelEditComment}
                                          className={softBtnGhost}
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className={RESP_TEXT}>
                                      {renderMentionRichText(
                                        comment.text,
                                        (comment as any).mentions,
                                        handleOpenUserProfile,
                                      )}
                                    </div>
                                  )}

                                  <div
                                    className={`${RESP_ACTION_ROW} ${RESP_TIME} -ml-[42px] mt-2 pl-0`}
                                  >
                                    <span>
                                      {timeAgo(
                                        comment.timestamp || comment.createdAt,
                                      )}
                                    </span>

                                    <div className="relative">
                                      {openReactionCommentKey ===
                                        commentReactionKey && (
                                        <div
                                          className="roddo-reaction-tray absolute bottom-[calc(100%+8px)] left-0 z-30 flex items-center gap-1 rounded-full border border-white/15 bg-[#181235]/95 px-2 py-1.5 shadow-[0_16px_34px_rgba(0,0,0,0.42)]  dark:bg-[#181235]/95"
                                          onMouseDown={(e) =>
                                            e.preventDefault()
                                          }
                                        >
                                          {COMMUNITY_REACTIONS.map(
                                            (reaction) => (
                                              <button
                                                key={reaction.key}
                                                type="button"
                                                onClick={() =>
                                                  handleChooseCommentReaction(
                                                    p.id,
                                                    comment,
                                                    reaction.key,
                                                  )
                                                }
                                                className={`roddo-reaction-option grid h-9 w-9 place-items-center rounded-full ${reaction.tone} text-lg shadow-[0_6px_14px_rgba(0,0,0,0.18)]`}
                                                title={reaction.label}
                                                aria-label={reaction.label}
                                              >
                                                <span aria-hidden="true">
                                                  {reaction.emoji}
                                                </span>
                                              </button>
                                            ),
                                          )}
                                        </div>
                                      )}

                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleMainCommentReactionClick(
                                            p.id,
                                            comment,
                                          )
                                        }
                                        onMouseDown={() =>
                                          startCommentReactionHold(
                                            commentReactionKey,
                                          )
                                        }
                                        onMouseUp={
                                          clearCommentReactionHoldTimer
                                        }
                                        onMouseLeave={
                                          clearCommentReactionHoldTimer
                                        }
                                        onTouchStart={() =>
                                          startCommentReactionHold(
                                            commentReactionKey,
                                          )
                                        }
                                        onTouchEnd={
                                          clearCommentReactionHoldTimer
                                        }
                                        onContextMenu={(e) =>
                                          e.preventDefault()
                                        }
                                        disabled={pending}
                                        className={RESP_LIKE_BTN(
                                          commentLikedByMe,
                                          pending,
                                        )}
                                        title={`Tap to react. Press and hold for more reactions${commentLikeCount > 0 ? ` (${commentLikeCount})` : ""}`}
                                        aria-label={`${commentLikedByMe ? commentReaction.label : "React"}${commentLikeCount > 0 ? `, ${commentLikeCount}` : ""}`}
                                      >
                                        <span
                                          className="text-sm leading-none"
                                          aria-hidden="true"
                                        >
                                          {commentLikedByMe
                                            ? commentReaction.emoji
                                            : "♡"}
                                        </span>
                                        {commentLikeCount > 0 ? (
                                          <span>{commentLikeCount}</span>
                                        ) : null}
                                      </button>
                                    </div>

                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleReplyToComment(
                                          p.id,
                                          comment.id,
                                          commentAuthorName,
                                          (comment as any).authorUsername ||
                                            (comment as any).username ||
                                            null,
                                        )
                                      }
                                      className={RESP_ACTION_LIKE_REPLY}
                                      title="Reply"
                                      aria-label="Reply"
                                    >
                                      ↩
                                    </button>
                                  </div>
                                </div>
                              </div>

                              <MiniMenu
                                disabled={editingCommentId === comment.id}
                                onEdit={
                                  isOwnComment
                                    ? () => startEditComment(comment)
                                    : undefined
                                }
                                onDelete={
                                  isOwnComment
                                    ? () =>
                                        setCommentToDelete({
                                          postId: p.id,
                                          commentId: comment.id,
                                        })
                                    : undefined
                                }
                              />
                            </div>
                          </div>

                          {visibleReplies.length > 0 && (
                            <div className="ml-5 space-y-1.5 border-l border-black/10 dark:border-white/12 pl-3">
                              {visibleReplies.map((reply: any) => {
                                const rMeta = getCommentLikeMeta(p.id, reply);
                                const rLegacyLikedBy = Array.isArray(
                                  reply.likedBy,
                                )
                                  ? reply.likedBy
                                  : [];
                                const replyLikeCount =
                                  rMeta?.count ?? rLegacyLikedBy.length;
                                const replyLikedByMe = Boolean(
                                  rMeta?.likedByMe ??
                                  rLegacyLikedBy.includes(uid),
                                );
                                const replyReaction = getCommunityReactionByKey(
                                  rMeta?.reaction ||
                                    (replyLikedByMe ? "like" : null),
                                );
                                const replyReactionKey = `${p.id}__${reply.id}`;
                                const replyPending = pendingCommentLikeKeys.has(
                                  `${p.id}:${reply.id}`,
                                );
                                const isOwnReply = reply.authorId === uid;
                                const isReplyHighlighted =
                                  highlightCommentKey ===
                                  `${p.id}__${reply.id}`;
                                const replyAuthorId = String(
                                  reply.authorId || "",
                                );
                                const replyAuthor = users?.[
                                  replyAuthorId
                                ] as any;
                                const replyAuthorName = String(
                                  replyAuthor?.displayName ||
                                    replyAuthor?.name ||
                                    replyAuthor?.username ||
                                    reply.authorName ||
                                    "Angler",
                                );
                                const replyAuthorAvatar = pickAvatarUrl(
                                  replyAuthor?.avatar,
                                  replyAuthor?.avatarUrl,
                                  replyAuthor?.photoURL,
                                  replyAuthor?.photoUrl,
                                  reply.authorAvatar,
                                  reply.authorAvatarUrl,
                                  reply.authorPhotoURL,
                                  reply.photoURL,
                                );

                                return (
                                  <div
                                    key={reply.id}
                                    id={`comment-${p.id}-${reply.id}`}
                                    className={`${RESP_CARD} transition-all ${isReplyHighlighted ? "ring-4 ring-[#ffcd64]/70" : ""}`}
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex min-w-0 flex-1 items-start gap-2">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleOpenUserProfile(replyAuthorId)
                                          }
                                          className="mt-0.5 h-7 w-7 shrink-0 overflow-hidden rounded-full border border-[#a78bfa]/35 bg-white/10 shadow-[0_3px_9px_rgba(89,65,37,0.05)] transition hover:-translate-y-0.5"
                                          title={t(
                                            "community.viewProfile",
                                            "View profile",
                                          )}
                                          aria-label={t(
                                            "community.viewProfile",
                                            "View profile",
                                          )}
                                        >
                                          <img
                                            src={replyAuthorAvatar}
                                            alt={replyAuthorName}
                                            className="h-full w-full object-cover"
                                          />
                                        </button>

                                        <div className="min-w-0 flex-1">
                                          <div className={RESP_USER}>
                                            <div className="flex min-w-0 items-center">
                                              <button
                                                type="button"
                                                className="truncate hover:underline"
                                                onClick={() =>
                                                  handleOpenUserProfile(
                                                    replyAuthorId,
                                                  )
                                                }
                                              >
                                                {replyAuthorName}
                                              </button>
                                              <BadgeIconRow
                                                reputation={resolveAuthorReputation(
                                                  reply,
                                                  replyAuthor,
                                                )}
                                                max={2}
                                              />
                                            </div>
                                          </div>

                                          {editingCommentId === reply.id ? (
                                            <div className="mt-2 space-y-2">
                                              <textarea
                                                rows={3}
                                                value={editingCommentDraft}
                                                onChange={(e) =>
                                                  setEditingCommentDraft(
                                                    e.target.value,
                                                  )
                                                }
                                                className={`w-full rounded-2xl ${inputShell} px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffcd64]/70`}
                                                style={{
                                                  background: panelBg,
                                                  color:
                                                    ui.textPrimary ??
                                                    theme.text,
                                                  borderColor: theme.border,
                                                }}
                                              />
                                              <div className="flex items-center gap-2">
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    saveEditComment(
                                                      p.id,
                                                      reply.id,
                                                    )
                                                  }
                                                  disabled={
                                                    !editingCommentDraft.trim() ||
                                                    isSavingCommentEdit
                                                  }
                                                  className={yellowBtn}
                                                >
                                                  {isSavingCommentEdit
                                                    ? "Saving…"
                                                    : "Save"}
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={cancelEditComment}
                                                  className={softBtnGhost}
                                                >
                                                  Cancel
                                                </button>
                                              </div>
                                            </div>
                                          ) : (
                                            <div className={RESP_TEXT}>
                                              {renderMentionRichText(
                                                reply.text,
                                                (reply as any).mentions,
                                                handleOpenUserProfile,
                                              )}
                                            </div>
                                          )}

                                          <div
                                            className={`${RESP_ACTION_ROW} ${RESP_TIME} -ml-[36px] mt-2 pl-0`}
                                          >
                                            <span>
                                              {timeAgo(
                                                reply.timestamp ||
                                                  reply.createdAt,
                                              )}
                                            </span>

                                            <div className="relative">
                                              {openReactionCommentKey ===
                                                replyReactionKey && (
                                                <div
                                                  className="roddo-reaction-tray absolute bottom-[calc(100%+8px)] left-0 z-30 flex items-center gap-1 rounded-full border border-white/15 bg-[#181235]/95 px-2 py-1.5 shadow-[0_16px_34px_rgba(0,0,0,0.42)]  dark:bg-[#181235]/95"
                                                  onMouseDown={(e) =>
                                                    e.preventDefault()
                                                  }
                                                >
                                                  {COMMUNITY_REACTIONS.map(
                                                    (reaction) => (
                                                      <button
                                                        key={reaction.key}
                                                        type="button"
                                                        onClick={() =>
                                                          handleChooseCommentReaction(
                                                            p.id,
                                                            reply,
                                                            reaction.key,
                                                          )
                                                        }
                                                        className={`roddo-reaction-option grid h-9 w-9 place-items-center rounded-full ${reaction.tone} text-lg shadow-[0_6px_14px_rgba(0,0,0,0.18)]`}
                                                        title={reaction.label}
                                                        aria-label={
                                                          reaction.label
                                                        }
                                                      >
                                                        <span aria-hidden="true">
                                                          {reaction.emoji}
                                                        </span>
                                                      </button>
                                                    ),
                                                  )}
                                                </div>
                                              )}

                                              <button
                                                type="button"
                                                onClick={() =>
                                                  handleMainCommentReactionClick(
                                                    p.id,
                                                    reply,
                                                  )
                                                }
                                                onMouseDown={() =>
                                                  startCommentReactionHold(
                                                    replyReactionKey,
                                                  )
                                                }
                                                onMouseUp={
                                                  clearCommentReactionHoldTimer
                                                }
                                                onMouseLeave={
                                                  clearCommentReactionHoldTimer
                                                }
                                                onTouchStart={() =>
                                                  startCommentReactionHold(
                                                    replyReactionKey,
                                                  )
                                                }
                                                onTouchEnd={
                                                  clearCommentReactionHoldTimer
                                                }
                                                onContextMenu={(e) =>
                                                  e.preventDefault()
                                                }
                                                disabled={replyPending}
                                                className={RESP_LIKE_BTN(
                                                  replyLikedByMe,
                                                  replyPending,
                                                )}
                                                title={`Tap to react. Press and hold for more reactions${replyLikeCount > 0 ? ` (${replyLikeCount})` : ""}`}
                                                aria-label={`${replyLikedByMe ? replyReaction.label : "React"}${replyLikeCount > 0 ? `, ${replyLikeCount}` : ""}`}
                                              >
                                                <span
                                                  className="text-sm leading-none"
                                                  aria-hidden="true"
                                                >
                                                  {replyLikedByMe
                                                    ? replyReaction.emoji
                                                    : "♡"}
                                                </span>
                                                {replyLikeCount > 0 ? (
                                                  <span>{replyLikeCount}</span>
                                                ) : null}
                                              </button>
                                            </div>

                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleReplyToComment(
                                                  p.id,
                                                  comment.id,
                                                  replyAuthorName,
                                                  (reply as any)
                                                    .authorUsername ||
                                                    (reply as any).username ||
                                                    null,
                                                )
                                              }
                                              className={RESP_ACTION_LIKE_REPLY}
                                              title="Reply"
                                              aria-label="Reply"
                                            >
                                              ↩
                                            </button>
                                          </div>
                                        </div>
                                      </div>

                                      <MiniMenu
                                        disabled={editingCommentId === reply.id}
                                        onEdit={
                                          isOwnReply
                                            ? () => startEditComment(reply)
                                            : undefined
                                        }
                                        onDelete={
                                          isOwnReply
                                            ? () =>
                                                setCommentToDelete({
                                                  postId: p.id,
                                                  commentId: reply.id,
                                                })
                                            : undefined
                                        }
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {!isExpanded &&
                            replies.length > REPLIES_PREVIEW_LIMIT && (
                              <button
                                type="button"
                                onClick={() =>
                                  setExpandedPosts((prev) => ({
                                    ...prev,
                                    [p.id]: true,
                                  }))
                                }
                                className="ml-5 text-xs font-semibold text-[var(--community-text-secondary)] hover:underline"
                              >
                                View {replies.length - REPLIES_PREVIEW_LIMIT}{" "}
                                more repl
                                {replies.length - REPLIES_PREVIEW_LIMIT === 1
                                  ? "y"
                                  : "ies"}
                              </button>
                            )}
                        </li>
                      );
                    })}
                  </ul>
                )}

                {!isExpanded &&
                  displayCommentCount > COMMENTS_PREVIEW_LIMIT && (
                    <button
                      type="button"
                      onClick={() => {
                        onCommentsOpened?.(p.id);
                        setExpandedPosts((prev) => ({ ...prev, [p.id]: true }));
                      }}
                      className="text-xs font-semibold text-[var(--roddo-text-light-soft)] hover:underline dark:text-[var(--roddo-text-dark-soft)]"
                    >
                      View all {displayCommentCount} comment
                      {displayCommentCount === 1 ? "" : "s"}
                    </button>
                  )}

                {isExpanded && displayCommentCount > COMMENTS_PREVIEW_LIMIT && (
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedPosts((prev) => ({ ...prev, [p.id]: false }))
                    }
                    className="text-xs font-semibold text-[var(--roddo-text-light-soft)] hover:underline dark:text-[var(--roddo-text-dark-soft)]"
                  >
                    Show fewer comments
                  </button>
                )}

                {replyTargets[p.id] && (
                  <div
                    className="flex items-center justify-between rounded-2xl border border-black/10 dark:border-white/12 px-3 py-2 text-xs text-[var(--community-text-secondary)]"
                    style={{
                      background: panelBg,
                      borderColor: theme.border,
                    }}
                  >
                    <div>
                      Replying to{" "}
                      <span className="font-bold">
                        @{replyTargets[p.id]?.authorName}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => cancelReply(p.id)}
                      className={dangerLink}
                    >
                      Cancel
                    </button>
                  </div>
                )}

                <FastCommentComposer
                  postId={p.id}
                  seed={commentDrafts[p.id] || ""}
                  pending={pendingCommentSubmitIds.has(p.id)}
                  mentionableUsers={mentionableUsers}
                  placeholder={t("community.writeComment", "Write a comment…")}
                  sendLabel={t("community.send", "Send")}
                  sendingLabel={t("community.sending", "Sending…")}
                  panelBg={panelBg}
                  textColor={ui.textPrimary ?? theme.text}
                  secondaryColor={ui.textSecondary ?? theme.text}
                  borderColor={theme.border}
                  onSubmit={(text) => handleSubmitComment(p.id, text)}
                />
              </div>
            </div>
          );
        })}

        {isBootstrapped &&
          active === "feed" &&
          onLoadMorePosts &&
          hasMorePosts && (
            <div className="mt-4 flex justify-center pb-10">
              <button
                type="button"
                onClick={() => onLoadMorePosts()}
                disabled={isLoadingMorePosts}
                className={[
                  "px-5 py-3 rounded-2xl",
                  "bg-[var(--community-surface-bg)]",
                  "border border-white/40 dark:border-white/12",
                  "shadow-[0_10px_18px_rgba(0,0,0,0.10)]",
                  "active:scale-[0.99] transition",
                  isLoadingMorePosts ? "opacity-60 cursor-not-allowed" : "",
                ].join(" ")}
              >
                {isLoadingMorePosts ? "Loading…" : "Load older posts"}
              </button>
            </div>
          )}
      </div>

      {editingPost && portalTarget &&
        createPortal(
          <div
            className="fixed inset-x-0 z-[1045] flex items-start justify-center px-3 pt-4"
            data-roddo-theme={isDarkMode ? "dark" : "light"}
            style={{
              ...viewVars,
              top: "var(--roddo-header-h, 84px)",
              bottom: "var(--roddo-bottom-nav-h, var(--roddo-nav-h, 76px))",
              color: ui.textPrimary ?? theme.text,
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="community-edit-post-title"
          >
            <button
              type="button"
              className="absolute inset-0 cursor-default bg-black/45 backdrop-blur-[2px]"
              aria-label="Close edit post"
              onClick={() => {
                if (isSubmittingPost || isUploadingImages) return;
                clearComposer();
              }}
            />

            <div
              className="relative z-10 flex max-h-full w-full max-w-[620px] flex-col overflow-hidden rounded-[22px] border shadow-[0_24px_80px_rgba(0,0,0,0.42)]"
              style={{
                background: ui.cardBgStrong ?? theme.panelStrong ?? theme.panel,
                borderColor: ui.borderStrong ?? theme.border,
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <div
                className="relative flex items-center gap-3 border-b px-4 py-3.5"
                style={{ borderColor: ui.border ?? theme.border }}
              >
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-[#d9b85c]/35">
                  <img
                    src={pickAvatarUrl(
                      (currentUserProfile as any).avatar,
                      (currentUserProfile as any).avatarUrl,
                      (currentUserProfile as any).photoURL,
                      (currentUserProfile as any).photoUrl,
                    )}
                    alt={currentUserProfile?.name || "You"}
                    className="h-full w-full object-cover"
                  />
                </div>

                <div className="min-w-0 flex-1 pr-10">
                  <div
                    id="community-edit-post-title"
                    className="text-[15px] font-black text-[var(--community-text-primary)]"
                  >
                    {t("community.editing", "Edit post")}
                  </div>
                  <div className="mt-0.5 truncate text-[11px] font-semibold text-[var(--community-text-secondary)]">
                    {selectedBayMeta.emoji} {selectedBayMeta.shortLabel} · {selectedDockMeta.emoji} {selectedDockMeta.shortLabel}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (isSubmittingPost || isUploadingImages) return;
                    clearComposer();
                  }}
                  disabled={isSubmittingPost || isUploadingImages}
                  className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full border border-[var(--roddo-border-light)] bg-black/5 text-[var(--community-text-primary)] transition hover:bg-black/10 disabled:cursor-not-allowed disabled:opacity-50 dark:border-[var(--roddo-border-dark)] dark:bg-white/8 dark:hover:bg-white/12"
                  aria-label={t("common.close", "Close")}
                  title={t("common.close", "Close")}
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </div>

              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4 overscroll-contain">
                <FastComposerTextArea
                  seed={newPostText}
                  seedVersion={composerTextVersion}
                  placeholder={composerPlaceholder}
                  inputBg={inputBg}
                  color={ui.textPrimary ?? theme.text}
                  borderColor={theme.border}
                  onTextRefChange={handleComposerTextRefChange}
                  onPresenceChange={handleComposerPresenceChange}
                  onDropFiles={(files) => addPickedImages(files)}
                />

                {composerImages.length > 0 ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-[11px] font-black text-[var(--community-text-primary)]">
                        Arrange photos
                      </div>
                      <div className="text-[10px] font-semibold text-[var(--community-text-secondary)]">
                        Tap a photo to make it main
                      </div>
                    </div>

                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {composerImages.map((item, idx) => (
                        <div
                          key={item.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => makePickedImagePrimary(idx)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              makePickedImagePrimary(idx);
                            }
                          }}
                          className={`relative h-24 w-24 shrink-0 overflow-hidden rounded-[14px] border text-left transition ${
                            idx === 0
                              ? "border-[#ffcd64] ring-2 ring-[#ffcd64]/35"
                              : "border-black/10 hover:border-[#ffcd64]/55 dark:border-white/10"
                          }`}
                          style={{ background: previewBg }}
                          title={idx === 0 ? "Main photo" : "Tap to make main photo"}
                          aria-label={idx === 0 ? "Main photo" : `Make photo ${idx + 1} the main photo`}
                        >
                          <img
                            src={item.previewUrl}
                            alt={`Preview ${idx + 1}`}
                            className="h-full w-full object-cover"
                          />

                          <div className="absolute left-1 top-1 flex items-center gap-1">
                            <span className="rounded-full bg-black/70 px-2 py-1 text-[9px] font-black text-white">
                              {idx === 0 ? "MAIN" : `#${idx + 1}`}
                            </span>
                          </div>

                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              removePickedImageAt(idx);
                            }}
                            className="absolute right-1 top-1 rounded-full bg-black/70 px-2 py-1 text-[10px] text-white"
                            title="Remove"
                            aria-label={`Remove photo ${idx + 1}`}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {composerDetailsOpen ? (
                  <div className="space-y-3 rounded-[14px] border border-[var(--roddo-border-light)] bg-[var(--roddo-panel-light-strong)] p-3 dark:border-[var(--roddo-border-dark)] dark:bg-[var(--roddo-panel-dark-strong)]">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-[12px] font-black text-[var(--community-text-primary)]">Gear details</div>
                        <div className="mt-0.5 text-[10px] font-semibold text-[var(--community-text-secondary)]">Optional tags for your setup</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setComposerDetailsOpen(false);
                          setActiveTagEditor(null);
                        }}
                        className="text-[11px] font-black text-[var(--community-text-secondary)]"
                      >
                        Done
                      </button>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <TagChip
                        label={tagRod.trim() ? `Rod: ${tagRod.trim()}` : "Rod"}
                        active={activeTagEditor === "rod"}
                        onClick={() => setActiveTagEditor((v) => (v === "rod" ? null : "rod"))}
                      />
                      <TagChip
                        label={tagReel.trim() ? `Reel: ${tagReel.trim()}` : "Reel"}
                        active={activeTagEditor === "reel"}
                        onClick={() => setActiveTagEditor((v) => (v === "reel" ? null : "reel"))}
                      />
                      <TagChip
                        label={tagLureOrBait.trim() ? `${tagLBType}: ${tagLureOrBait.trim()}` : "Lure/Bait"}
                        active={activeTagEditor === "lb"}
                        onClick={() => setActiveTagEditor((v) => (v === "lb" ? null : "lb"))}
                      />
                    </div>

                    {activeTagEditor === "rod" ? (
                      <input
                        value={tagRod}
                        onChange={(e) => setTagRod(e.target.value)}
                        placeholder="Rod name"
                        className={`w-full rounded-[12px] ${inputShell} px-3 py-2.5 text-sm focus:outline-none`}
                        style={{ background: inputBg, color: ui.textPrimary ?? theme.text, borderColor: theme.border }}
                      />
                    ) : null}

                    {activeTagEditor === "reel" ? (
                      <input
                        value={tagReel}
                        onChange={(e) => setTagReel(e.target.value)}
                        placeholder="Reel name"
                        className={`w-full rounded-[12px] ${inputShell} px-3 py-2.5 text-sm focus:outline-none`}
                        style={{ background: inputBg, color: ui.textPrimary ?? theme.text, borderColor: theme.border }}
                      />
                    ) : null}

                    {activeTagEditor === "lb" ? (
                      <div className="grid gap-2 sm:grid-cols-[auto_auto_1fr]">
                        <button
                          type="button"
                          onClick={() => setTagLBType("Lure")}
                          className={`rounded-[11px] border px-3 py-2 text-xs font-black ${tagLBType === "Lure" ? "ring-2 ring-[#ffcd64]/35" : ""}`}
                          style={{ background: pillBg, color: pillText }}
                        >
                          Lure
                        </button>
                        <button
                          type="button"
                          onClick={() => setTagLBType("Bait")}
                          className={`rounded-[11px] border px-3 py-2 text-xs font-black ${tagLBType === "Bait" ? "ring-2 ring-[#ffcd64]/35" : ""}`}
                          style={{ background: pillBg, color: pillText }}
                        >
                          Bait
                        </button>
                        <input
                          value={tagLureOrBait}
                          onChange={(e) => setTagLureOrBait(e.target.value)}
                          placeholder={`${tagLBType} name`}
                          className={`min-w-0 rounded-[12px] ${inputShell} px-3 py-2.5 text-sm focus:outline-none`}
                          style={{ background: inputBg, color: ui.textPrimary ?? theme.text, borderColor: theme.border }}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {isUploadingImages ? (
                  <div className={`text-xs ${txtSub}`}>{t("community.uploading", "Uploading photos…")}</div>
                ) : null}
              </div>

              <div
                className="flex items-center gap-2 border-t px-4 py-3.5"
                style={{ borderColor: ui.border ?? theme.border }}
              >
                <button
                  type="button"
                  onClick={() => {
                    const input = document.getElementById("community-edit-image-input") as HTMLInputElement | null;
                    input?.click();
                  }}
                  className="inline-flex h-10 items-center gap-2 rounded-[12px] border border-[var(--roddo-border-light)] px-3 text-[12px] font-black text-[var(--community-text-primary)] dark:border-[var(--roddo-border-dark)]"
                >
                  <CameraIcon className="h-4 w-4" />
                  Photo
                </button>
                <input
                  id="community-edit-image-input"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    void addPickedImages(e.target.files);
                    e.currentTarget.value = "";
                  }}
                />

                <button
                  type="button"
                  onClick={() => setComposerDetailsOpen((value) => !value)}
                  className="inline-flex h-10 items-center rounded-[12px] border border-[var(--roddo-border-light)] px-3 text-[12px] font-black text-[var(--community-text-primary)] dark:border-[var(--roddo-border-dark)]"
                >
                  {composerDetailsOpen ? "Hide details" : composerTagCount ? `Details · ${composerTagCount}` : "Add details"}
                </button>

                <button
                  type="button"
                  onClick={handleCreateOrEditPost}
                  disabled={!canSubmit || isSubmittingPost}
                  className={`ml-auto h-10 rounded-[12px] px-4 text-[12px] font-black transition ${
                    canSubmit
                      ? "bg-[#ffcd64] text-[#2d2b42] hover:-translate-y-0.5"
                      : "bg-[var(--community-surface-bg)] text-[var(--community-text-secondary)] opacity-60"
                  }`}
                >
                  {isSubmittingPost ? t("community.saving", "Saving…") : t("community.save", "Save")}
                </button>
              </div>
            </div>
          </div>,
          portalTarget,
        )}

      {gallery.open && gallery.images.length > 0 && (
        <GalleryLightbox
          images={gallery.images}
          startIndex={gallery.startIndex}
          title={gallery.title}
          onClose={closeGallery}
        />
      )}

      {reportingTarget && (
        <CommunityReportModal
          target={reportingTarget}
          reason={reportReason}
          details={reportDetails}
          isSubmitting={isSubmittingReport}
          onReasonChange={setReportReason}
          onDetailsChange={setReportDetails}
          onCancel={() => {
            if (isSubmittingReport) return;
            setReportingTarget(null);
            setReportReason("spam");
            setReportDetails("");
          }}
          onSubmit={handleSubmitUserReport}
        />
      )}

      {commentToDelete && (
        <ConfirmationModal
          title={t("community.confirmDeleteCommentTitle", "Delete comment?")}
          description={t(
            "community.confirmDeleteCommentDescription",
            "This will permanently delete this comment.",
          )}
          confirmLabel={t("common.delete", "Delete")}
          cancelLabel={t("common.cancel", "Cancel")}
          onConfirm={async () => {
            const target = commentToDelete;
            setCommentToDelete(null);

            if (!target) return;

            try {
              await Promise.resolve(
                safeCommentDeleted(target.postId, target.commentId),
              );
              props.addToast(
                t("community.commentDeleted", "Comment deleted."),
                "success",
              );
            } catch (err) {
              console.error("Failed to delete comment", err);
              props.addToast(
                t("community.commentDeleteError", "Could not delete comment."),
                "error",
              );
            }
          }}
          onCancel={() => setCommentToDelete(null)}
        />
      )}

      {postToDelete && (
        <ConfirmationModal
          title={t("community.confirmDeleteTitle", "Delete post?")}
          description={t(
            "community.confirmDeleteDescription",
            "This cannot be undone.",
          )}
          confirmLabel={t("common.delete", "Delete")}
          cancelLabel={t("common.cancel", "Cancel")}
          onConfirm={handleConfirmDeletePost}
          onCancel={() => setPostToDelete(null)}
        />
      )}
    </div>
  );
}
